import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Eye,
  FileText,
  LoaderCircle,
  RefreshCw,
  Save,
  Search,
  Send,
  ShieldCheck,
  SquarePen,
  Trash2,
  XCircle
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { AgreementFormStudio } from "@/components/AgreementFormStudio";
import { AgreementPreview } from "@/components/AgreementPreview";
import { AgreementPreviewDownloadButton } from "@/components/AgreementPreviewDownloadButton";
import { ModalShell } from "@/components/ModalShell";
import { StatusPill } from "@/components/StatusPill";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { buildAgreementFormDefaults, createEmptyBlock } from "@/lib/agreement-form-schema";
import { cn } from "@/lib/utils";
import type {
  AgreementSubmissionSummary,
  CandidateAgreement,
  CandidateAgreementsRow,
  DashboardResponse,
  PendingAgreementSubmissionRow,
  TemplateVersion
} from "@/lib/api";
import {
  type AdminSubmissionDetail,
  type AdminTemplateForm,
  useAdminWorkspace
} from "@/pages/admin/AdminWorkspaceLayout";
import { fetchAdminSubmissionDetail, fetchApprovedAgreements, fetchCandidateAgreementsOverview, fetchPendingAgreements, reviewAgreementSubmission, analyzePdfForFields } from "@/lib/api";
import type { CandidateUser, PdfAnalysisResult, PdfDetectedSection } from "@/lib/api";

type EditableTemplateField = Exclude<keyof AdminTemplateForm, "formSchema">;
type TemplateEditorMode = "builder" | "document";

export function AdminUsersPage() {
  const {
    allUsers,
    handleApproval,
    handleDeleteCandidate,
    handleUpdateCandidate,
    isDeletingCandidateId,
    isUpdating,
    pendingUsers
  } = useAdminWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingCandidate, setEditingCandidate] = useState<DashboardResponse["users"][number] | null>(null);
  const [allPageIndex, setAllPageIndex] = useState(0);
  const [pendingPageIndex, setPendingPageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 8;
  const activeUserTab = searchParams.get("view") === "pending" ? "pending" : "all";

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredAllUsers = useMemo(
    () =>
      normalizedQuery
        ? allUsers.filter((u) =>
            [u.fullName, u.email, u.phone ?? ""].join(" ").toLowerCase().includes(normalizedQuery)
          )
        : allUsers,
    [allUsers, normalizedQuery]
  );
  const filteredPendingUsers = useMemo(
    () =>
      normalizedQuery
        ? pendingUsers.filter((u) =>
            [u.fullName, u.email, u.phone ?? ""].join(" ").toLowerCase().includes(normalizedQuery)
          )
        : pendingUsers,
    [normalizedQuery, pendingUsers]
  );

  const totalPages = Math.max(1, Math.ceil(filteredAllUsers.length / pageSize));
  const currentPageIndex = Math.min(allPageIndex, totalPages - 1);
  const pageStart = currentPageIndex * pageSize;
  const visibleUsers = filteredAllUsers.slice(pageStart, pageStart + pageSize);
  const resultStart = filteredAllUsers.length === 0 ? 0 : pageStart + 1;
  const resultEnd = Math.min(pageStart + visibleUsers.length, filteredAllUsers.length);
  const pendingTotalPages = Math.max(1, Math.ceil(filteredPendingUsers.length / pageSize));
  const currentPendingPageIndex = Math.min(pendingPageIndex, pendingTotalPages - 1);
  const pendingPageStart = currentPendingPageIndex * pageSize;
  const visiblePendingUsers = filteredPendingUsers.slice(pendingPageStart, pendingPageStart + pageSize);
  const pendingResultStart = filteredPendingUsers.length === 0 ? 0 : pendingPageStart + 1;
  const pendingResultEnd = Math.min(
    pendingPageStart + visiblePendingUsers.length,
    filteredPendingUsers.length
  );
  const usersCountLabel = `${allUsers.length} ${allUsers.length === 1 ? "user" : "users"}`;

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    setAllPageIndex(0);
    setPendingPageIndex(0);
  }

  function setActiveUserTab(tab: "all" | "pending") {
    const nextParams = new URLSearchParams(searchParams);

    if (tab === "pending") {
      nextParams.set("view", "pending");
    } else {
      nextParams.delete("view");
    }

    setSearchParams(nextParams, { replace: true });
  }

  return (
    <div className="mt-6">
      <section className="surface p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="eyebrow">Users</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
              Candidate directory
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
              Review candidate details, account status, and verification progress in one concise table.
            </p>
          </div>

          <label className="block w-full max-w-sm">
            <span className="sr-only">Search candidates</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/70" />
              <input
                className="input-field pl-11"
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by name, email, or phone"
                type="search"
                value={searchQuery}
              />
            </div>
          </label>
        </div>

        <div className="mt-4 flex items-center">
          <div className="rounded-[18px] border border-line/70 bg-cloud/55 px-4 py-3 text-sm font-medium text-slate">
            {usersCountLabel}
          </div>
        </div>

        <div
          aria-label="User views"
          className="mt-4 inline-flex flex-wrap gap-2 rounded-[18px] border border-line/70 bg-cloud/45 p-1"
          role="tablist"
        >
          <button
            aria-selected={activeUserTab === "all"}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              activeUserTab === "all"
                ? "bg-navy text-white shadow-sm"
                : "text-slate hover:bg-white/80 hover:text-ink"
            }`}
            data-testid="admin-users-tab-all"
            onClick={() => setActiveUserTab("all")}
            role="tab"
            type="button"
          >
            Users
            <span className="ml-2 text-xs opacity-75">{allUsers.length}</span>
          </button>
          <button
            aria-selected={activeUserTab === "pending"}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              activeUserTab === "pending"
                ? "bg-navy text-white shadow-sm"
                : "text-slate hover:bg-white/80 hover:text-ink"
            }`}
            data-testid="admin-users-tab-pending"
            onClick={() => setActiveUserTab("pending")}
            role="tab"
            type="button"
          >
            Pending users
            <span className="ml-2 text-xs opacity-75">{pendingUsers.length}</span>
          </button>
        </div>

        {activeUserTab === "all" ? (
          <div className="mt-6 overflow-hidden rounded-[24px] border border-line/80 bg-white shadow-sm" data-testid="admin-users-table">
            {filteredAllUsers.length === 0 ? (
              <EmptyState
                hint={allUsers.length === 0 ? "Candidates will appear here once they register and verify their email." : undefined}
                text={allUsers.length === 0 ? "No candidate accounts are available yet." : "No candidates match your search."}
              />
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-line/80 bg-cloud/70 text-xs font-semibold uppercase tracking-[0.12em] text-slate/70">
                    <tr>
                      <th className="w-16 px-5 py-4">Sr No.</th>
                      <th className="min-w-[320px] px-5 py-4">Candidate</th>
                      <th className="min-w-[150px] px-5 py-4">Status</th>
                      <th className="min-w-[190px] px-5 py-4">Email verification</th>
                      <th className="min-w-[170px] px-5 py-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line/70">
                    {visibleUsers.map((candidate, index) => (
                      <tr
                        className="transition hover:bg-cloud/45"
                        data-testid={`admin-users-row-${candidate.id}`}
                        key={candidate.id}
                      >
                        <td className="px-5 py-4 align-top text-sm font-medium text-slate">
                          {pageStart + index + 1}
                        </td>
                        <td className="px-5 py-4 align-top">
                          <p className="font-semibold text-ink">{candidate.fullName}</p>
                          <p className="mt-1 text-sm font-medium text-slate">{candidate.email}</p>
                          <p className="mt-1 text-sm text-slate">{candidate.phone || "No phone added"}</p>
                        </td>
                        <td className="px-5 py-4 align-top">
                          <StatusPill status={candidate.status} />
                        </td>
                        <td className="px-5 py-4 align-top text-sm text-slate">
                          {candidate.emailVerifiedAt
                            ? formatDisplayDate(candidate.emailVerifiedAt)
                            : "Not verified"}
                        </td>
                        <td className="px-5 py-4 align-top">
                          <div className="flex flex-wrap justify-start gap-2 sm:justify-end">
                            <CandidateRowActions
                              candidate={candidate}
                              isDeleting={isDeletingCandidateId === candidate.id}
                              isSaving={isUpdating === candidate.id}
                              onDelete={handleDeleteCandidate}
                              onEdit={setEditingCandidate}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-3 border-t border-line/80 bg-cloud/35 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate" data-testid="admin-users-pagination-summary">
                  Showing {resultStart}-{resultEnd} of {allUsers.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="btn-secondary px-3 py-2 text-xs"
                    data-testid="admin-users-pagination-prev"
                    disabled={currentPageIndex === 0}
                    onClick={() => setAllPageIndex((current) => Math.max(0, current - 1))}
                    type="button"
                  >
                    Previous
                  </button>
                  <span
                    className="rounded-xl border border-line/70 bg-white px-3 py-2 text-xs font-semibold text-ink"
                    data-testid="admin-users-pagination-page"
                  >
                    Page {currentPageIndex + 1} of {totalPages}
                  </span>
                  <button
                    className="btn-secondary px-3 py-2 text-xs"
                    data-testid="admin-users-pagination-next"
                    disabled={currentPageIndex >= totalPages - 1}
                    onClick={() => setAllPageIndex((current) => Math.min(totalPages - 1, current + 1))}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              </div>
              </>
            )}
          </div>
        ) : (
          <div className="mt-6 overflow-hidden rounded-[24px] border border-line/80 bg-white shadow-sm" data-testid="admin-pending-users-table">
            {filteredPendingUsers.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  hint={pendingUsers.length === 0 ? "New registrations awaiting approval will appear here automatically." : undefined}
                  text={pendingUsers.length === 0 ? "No candidates are currently waiting for approval." : "No pending candidates match your search."}
                />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b border-line/80 bg-cloud/70 text-xs font-semibold uppercase tracking-[0.12em] text-slate/70">
                      <tr>
                        <th className="w-16 px-5 py-4">Sr No.</th>
                        <th className="min-w-[320px] px-5 py-4">Candidate</th>
                        <th className="min-w-[150px] px-5 py-4">Status</th>
                        <th className="min-w-[190px] px-5 py-4">Verified</th>
                        <th className="min-w-[340px] px-5 py-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line/70">
                      {visiblePendingUsers.map((candidate, index) => (
                        <tr
                          className="bg-amber-50/40 transition hover:bg-amber-50/70"
                          data-testid={`admin-pending-user-card-${candidate.id}`}
                          key={candidate.id}
                        >
                          <td className="px-5 py-4 align-top text-sm font-medium text-slate">
                            {pendingPageStart + index + 1}
                          </td>
                          <td className="px-5 py-4 align-top">
                            <p className="font-semibold text-ink">{candidate.fullName}</p>
                            <p className="mt-1 text-sm font-medium text-slate">{candidate.email}</p>
                            <p className="mt-1 text-sm text-slate">{candidate.phone || "No phone added"}</p>
                          </td>
                          <td className="px-5 py-4 align-top">
                            <StatusPill status={candidate.status} />
                          </td>
                          <td className="px-5 py-4 align-top text-sm text-slate">
                            {candidate.emailVerifiedAt
                              ? formatDisplayDate(candidate.emailVerifiedAt)
                              : "Not verified"}
                          </td>
                          <td className="px-5 py-4 align-top">
                            <div className="flex flex-wrap justify-start gap-2 sm:justify-end">
                              <CandidateRowActions
                                candidate={candidate}
                                isDeleting={isDeletingCandidateId === candidate.id}
                                isSaving={isUpdating === candidate.id}
                                onDelete={handleDeleteCandidate}
                                onEdit={setEditingCandidate}
                              />
                              <CandidateApprovalActions
                                candidate={candidate}
                                isSaving={isUpdating === candidate.id}
                                onDecision={handleApproval}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col gap-3 border-t border-line/80 bg-cloud/35 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate" data-testid="admin-pending-users-pagination-summary">
                    Showing {pendingResultStart}-{pendingResultEnd} of {pendingUsers.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      className="btn-secondary px-3 py-2 text-xs"
                      data-testid="admin-pending-users-pagination-prev"
                      disabled={currentPendingPageIndex === 0}
                      onClick={() => setPendingPageIndex((current) => Math.max(0, current - 1))}
                      type="button"
                    >
                      Previous
                    </button>
                    <span
                      className="rounded-xl border border-line/70 bg-white px-3 py-2 text-xs font-semibold text-ink"
                      data-testid="admin-pending-users-pagination-page"
                    >
                      Page {currentPendingPageIndex + 1} of {pendingTotalPages}
                    </span>
                    <button
                      className="btn-secondary px-3 py-2 text-xs"
                      data-testid="admin-pending-users-pagination-next"
                      disabled={currentPendingPageIndex >= pendingTotalPages - 1}
                      onClick={() =>
                        setPendingPageIndex((current) => Math.min(pendingTotalPages - 1, current + 1))
                      }
                      type="button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {editingCandidate ? (
          <CandidateEditDialog
            candidate={editingCandidate}
            isSaving={isUpdating === editingCandidate.id}
            onClose={() => setEditingCandidate(null)}
            onSave={async (payload) => {
              await handleUpdateCandidate(editingCandidate.id, payload);
              setEditingCandidate(null);
            }}
          />
        ) : null}
      </section>
    </div>
  );
}

export function AdminPendingAgreementsPage() {
  const {
    handleSubmissionDecision,
    isUpdating,
    selectSubmission,
    selectedSubmission,
    selectedSubmissionId
  } = useAdminWorkspace();
  const [selectedSubmissionPdfUrl, setSelectedSubmissionPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedSubmission?.pdfBase64) {
      setSelectedSubmissionPdfUrl(null);
      return;
    }

    try {
      const bytes = Uint8Array.from(
        atob(selectedSubmission.pdfBase64),
        (char) => char.charCodeAt(0)
      );
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setSelectedSubmissionPdfUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } catch (e) {
      console.error("Failed to generate PDF Blob URL for selected submission", e);
      setSelectedSubmissionPdfUrl(null);
    }
  }, [selectedSubmission?.pdfBase64]);

  const [pendingSubmissions, setPendingSubmissions] = useState<PendingAgreementSubmissionRow[]>([]);
  const [approvedSubmissions, setApprovedSubmissions] = useState<PendingAgreementSubmissionRow[]>([]);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [approvedError, setApprovedError] = useState<string | null>(null);

  useEffect(() => {
    void refreshPendingAgreements();
    void refreshApprovedAgreements();
  }, []);

  async function refreshPendingAgreements() {
    try {
      const response = await fetchPendingAgreements();
      setPendingSubmissions(response.submissions);
      setPendingError(null);
      return response.submissions;
    } catch (error) {
      setPendingError(error instanceof Error ? error.message : "Unable to load pending agreements.");
      return [];
    }
  }

  async function refreshApprovedAgreements() {
    try {
      const response = await fetchApprovedAgreements();
      setApprovedSubmissions(response.submissions);
      setApprovedError(null);
    } catch (error) {
      setApprovedError(error instanceof Error ? error.message : "Unable to load approved agreements.");
    }
  }
  const [feedback, setFeedback] = useState("");
  const [authorizedSignatoryName, setAuthorizedSignatoryName] = useState("");
  const [authorizedSignatoryDesignation, setAuthorizedSignatoryDesignation] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [modalError, setModalError] = useState<string | null>(null);

  const isAddendum =
    (selectedSubmission as any)?.template?.slug === "addendum-offer-letter-india" ||
    Boolean(selectedSubmission?.templateVersion?.title?.toLowerCase().includes("addendum"));

  useEffect(() => {
    if (!selectedSubmission) {
      return;
    }

    setModalError(null);
    setFeedback((selectedSubmission as any).reviewComment ?? "");

    if (!isAddendum) {
      return;
    }

    const formData = ((selectedSubmission as any).formData ?? {}) as Record<string, unknown>;
    setAuthorizedSignatoryName(
      typeof formData.authorizedSignatoryName === "string" ? formData.authorizedSignatoryName : ""
    );
    setAuthorizedSignatoryDesignation(
      typeof formData.authorizedSignatoryDesignation === "string"
        ? formData.authorizedSignatoryDesignation
        : ""
    );
    setEmployeeName(
      typeof formData.signatureName === "string" && formData.signatureName.trim().length > 0
        ? formData.signatureName
        : ((selectedSubmission as any)?.candidate?.fullName ?? "")
    );
  }, [selectedSubmissionId]);

  function downloadPdfBase64(payload: { base64: string; fileName: string }) {
    const bytes = Uint8Array.from(atob(payload.base64), (char) => char.charCodeAt(0));
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = payload.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const selectedCandidate = (selectedSubmission as any)?.candidate as
    | { email: string; fullName: string }
    | undefined;
  const downloadFileName = selectedCandidate?.fullName
    ? `agreement-${selectedCandidate.fullName.replace(/\s+/g, "-").toLowerCase()}-v${selectedSubmission?.versionNumber ?? 1}.pdf`
    : `agreement-v${selectedSubmission?.versionNumber ?? 1}.pdf`;
  const [previewMode, setPreviewMode] = useState<"screen" | "pdf">("screen");
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const selectedCandidateId = (selectedSubmission as any)?.candidate?.id as string | undefined;

  const candidatePendingQueue = useMemo(() => {
    if (!selectedCandidateId) return [];
    const queue = pendingSubmissions.filter((row) => row.candidate.id === selectedCandidateId);
    const order = (slug: string) =>
      slug === "internship-agreement-india" ? 0 : slug === "addendum-offer-letter-india" ? 1 : 99;
    return [...queue].sort((a, b) => order(a.template.slug) - order(b.template.slug));
  }, [pendingSubmissions, selectedCandidateId]);

  const currentQueueIndex = useMemo(() => {
    if (!selectedSubmissionId) return -1;
    return candidatePendingQueue.findIndex((row) => row.id === selectedSubmissionId);
  }, [candidatePendingQueue, selectedSubmissionId]);

  const isReviewable = Boolean(selectedSubmission?.approvalStatus === "pending");

  const pendingGroups = useMemo(() => {
    const grouped = new Map<
      string,
      {
        candidate: PendingAgreementSubmissionRow["candidate"];
        internship?: PendingAgreementSubmissionRow;
        addendum?: PendingAgreementSubmissionRow;
      }
    >();

    for (const submission of pendingSubmissions) {
      const existing = grouped.get(submission.candidate.id) ?? {
        candidate: submission.candidate
      };

      if (submission.template.slug === "internship-agreement-india") {
        existing.internship = submission;
      } else if (submission.template.slug === "addendum-offer-letter-india") {
        existing.addendum = submission;
      }

      grouped.set(submission.candidate.id, existing);
    }

    return Array.from(grouped.values());
  }, [pendingSubmissions]);

  const approvedGroups = useMemo(() => {
    const grouped = new Map<
      string,
      {
        candidate: PendingAgreementSubmissionRow["candidate"];
        internship?: PendingAgreementSubmissionRow;
        addendum?: PendingAgreementSubmissionRow;
        latestApprovedAt?: string;
      }
    >();

    for (const submission of approvedSubmissions) {
      const existing = grouped.get(submission.candidate.id) ?? {
        candidate: submission.candidate
      };

      if (submission.template.slug === "internship-agreement-india") {
        existing.internship = submission;
      } else if (submission.template.slug === "addendum-offer-letter-india") {
        existing.addendum = submission;
      }

      if (submission.approvedAt) {
        existing.latestApprovedAt =
          !existing.latestApprovedAt || submission.approvedAt > existing.latestApprovedAt
            ? submission.approvedAt
            : existing.latestApprovedAt;
      }

      grouped.set(submission.candidate.id, existing);
    }

    return Array.from(grouped.values());
  }, [approvedSubmissions]);

  const [approvedCollegeFilter, setApprovedCollegeFilter] = useState<string>("all");
  const [approvedSearch, setApprovedSearch] = useState<string>("");

  const approvedCollegeOptions = useMemo(() => {
    const colleges = new Set(
      approvedGroups.map((group) => group.candidate.university).filter((value) => value?.trim())
    );
    return ["all", ...Array.from(colleges).sort((a, b) => a.localeCompare(b))];
  }, [approvedGroups]);

  const filteredApprovedGroups = useMemo(() => {
    const normalizedSearch = approvedSearch.trim().toLowerCase();

    return approvedGroups.filter((group) => {
      const collegeMatches =
        approvedCollegeFilter === "all" || group.candidate.university === approvedCollegeFilter;
      if (!collegeMatches) return false;

      if (!normalizedSearch) return true;

      const haystack = `${group.candidate.fullName} ${group.candidate.email} ${group.candidate.serialNumber} ${group.candidate.university}`
        .toLowerCase()
        .trim();
      return haystack.includes(normalizedSearch);
    });
  }, [approvedGroups, approvedCollegeFilter, approvedSearch]);

  function renderAgreementCell(submission: PendingAgreementSubmissionRow | undefined) {
    if (!submission) {
      return <span className="text-sm text-slate">—</span>;
    }

    const signedAt = (submission.summary as any)?.signedAt as string | undefined;
    const signedLabel = signedAt
      ? signedAt
      : submission.submittedAt
        ? formatDisplayDate(submission.submittedAt)
        : "—";

    return (
      <button
        className="group inline-flex w-full flex-col items-start gap-1 rounded-xl border border-line/70 bg-white px-3 py-2 text-left transition hover:border-royal/25 hover:bg-cloud/40"
        onClick={() => {
          void (async () => {
            await selectSubmission(submission.id);
            setIsPreviewExpanded(true);
          })();
        }}
        type="button"
      >
        <span className="text-sm font-semibold text-ink group-hover:text-navy">Open</span>
        <span className="text-xs font-medium text-slate">
          v{submission.versionNumber} · {signedLabel}
        </span>
      </button>
    );
  }

  return (
    <div className="mt-6 space-y-6" data-testid="admin-pending-agreements-page">
      <section className="surface p-6 md:p-7">
        <p className="eyebrow">Agreements</p>
        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
          Pending agreement approvals
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
          Review submitted agreements and approve or reject them. Approved submissions remain in the
          audit trail and can be downloaded as PDFs.
        </p>
      </section>

      <section className="surface p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-ink">Pending submissions</p>
              <p className="mt-1 text-sm text-slate">
                {pendingSubmissions.length} pending{" "}
                {pendingSubmissions.length === 1 ? "agreement" : "agreements"}
              </p>
            </div>
          </div>

          {pendingError ? (
            <div className="mt-5 rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700">
              {pendingError}
            </div>
          ) : null}

	          <div className="mt-5 overflow-hidden rounded-[24px] border border-line/70 bg-white/70">
	            <div className="overflow-x-auto">
	              <table className="min-w-full table-auto">
	                <thead className="bg-cloud/40">
	                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
	                    <th className="px-4 py-3">S.No</th>
	                    <th className="px-4 py-3">Candidate</th>
	                    <th className="px-4 py-3">College</th>
	                    <th className="px-4 py-3">Serial</th>
	                    <th className="px-4 py-3">Phone</th>
	                    <th className="px-4 py-3">Internship Agreement</th>
	                    <th className="px-4 py-3">Addendum Agreement</th>
	                    <th className="px-4 py-3">Status</th>
	                  </tr>
	                </thead>
	                <tbody className="divide-y divide-line/70">
	                  {pendingGroups.length === 0 ? (
	                    <tr>
	                      <td className="px-4 py-6 text-sm text-slate" colSpan={8}>
	                        <EmptyState text="No pending submissions." />
	                      </td>
	                    </tr>
	                  ) : (
	                    pendingGroups.map((group, index) => {
	                      const initials = group.candidate.fullName
	                        .split(" ")
	                        .filter(Boolean)
	                        .slice(0, 2)
	                        .map((word) => word[0]?.toUpperCase())
	                        .join("");
	                      const pendingCount = (group.internship ? 1 : 0) + (group.addendum ? 1 : 0);

	                      return (
	                        <tr className="transition hover:bg-cloud/30" key={group.candidate.id}>
	                          <td className="px-4 py-4 text-sm font-semibold text-ink">
	                            {index + 1}
	                          </td>
	                          <td className="px-4 py-4">
	                            <div className="flex items-center gap-3">
	                              <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-line/70 bg-white text-sm font-semibold text-royal">
	                                {initials || "C"}
	                              </div>
	                              <div className="min-w-0">
	                                <p className="truncate font-semibold text-ink">
	                                  {group.candidate.fullName}
	                                </p>
	                                <p className="truncate text-sm text-slate">
	                                  {group.candidate.email}
	                                </p>
	                              </div>
	                            </div>
	                          </td>
	                          <td className="px-4 py-4 text-sm text-slate">
	                            {group.candidate.university || "—"}
	                          </td>
	                          <td className="px-4 py-4 text-sm text-slate">
	                            {group.candidate.serialNumber || "—"}
	                          </td>
	                          <td className="px-4 py-4 text-sm font-semibold text-ink">
	                            {group.candidate.phone || "—"}
	                          </td>
	                          <td className="px-4 py-4">{renderAgreementCell(group.internship)}</td>
	                          <td className="px-4 py-4">{renderAgreementCell(group.addendum)}</td>
	                          <td className="px-4 py-4">
	                            <StatusPill status="pending" />
	                            <p className="mt-1 text-xs font-medium text-slate">
	                              {pendingCount} pending
	                            </p>
	                          </td>
	                        </tr>
	                      );
	                    })
	                  )}
	                </tbody>
	              </table>
	            </div>
	          </div>
      </section>

	      {isPreviewExpanded && selectedSubmission ? (
	        <div
	          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/35 px-4 py-6"
	          role="dialog"
	          aria-modal="true"
	        >
	          <div className="surface flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden p-0">
	            <div className="sticky top-0 z-10 border-b border-line/70 bg-white/95 px-6 py-5 backdrop-blur">
	              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
	              <div>
	                <p className="eyebrow">Agreement Preview</p>
	                <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
	                  {selectedCandidate?.fullName ?? "Candidate"} · v{selectedSubmission.versionNumber}
	                </h3>
	              </div>
	              <div className="flex flex-wrap gap-2 sm:justify-end">
	                <button
	                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
	                    previewMode === "screen"
	                      ? "border-navy bg-navy text-white shadow-sm"
	                      : "border-line bg-cloud/60 text-slate hover:border-royal/20 hover:bg-white hover:text-ink"
	                  }`}
	                  onClick={() => setPreviewMode("screen")}
	                  type="button"
	                >
	                  Screen
	                </button>
	                <button
	                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
	                    previewMode === "pdf"
	                      ? "border-navy bg-navy text-white shadow-sm"
	                      : "border-line bg-cloud/60 text-slate hover:border-royal/20 hover:bg-white hover:text-ink"
	                  }`}
	                  onClick={() => setPreviewMode("pdf")}
	                  type="button"
	                >
	                  PDF
	                </button>
	                <button
	                  className="btn-secondary px-4 py-2.5"
	                  onClick={() => setIsPreviewExpanded(false)}
	                  type="button"
	                >
	                  Close
	                </button>
	              </div>
	            </div>
	            </div>

	            <div className="flex-1 overflow-auto px-6 py-6">
	            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
	              <div className="overflow-hidden rounded-[24px] border border-line/70 bg-white">
	                {previewMode === "pdf" && selectedSubmission.pdfBase64 ? (
	                  <iframe
	                    className="h-[60vh] w-full sm:h-[70vh]"
	                    src={selectedSubmissionPdfUrl || ""}
	                    title="Agreement PDF preview"
	                  />
	                ) : (
	                  <div className="max-h-[70vh] overflow-auto p-6">
	                    <AgreementPreview
	                      agreement={{
                          ...((selectedSubmission.formData ?? {}) as CandidateAgreement),
                          signatureImageData: (selectedSubmission as any).signatureImageData
                        }}
	                      candidate={{
	                        email: selectedCandidate?.email ?? "",
                        fullName: selectedCandidate?.fullName ?? "",
                        phone: (selectedSubmission as any).candidate?.phone ?? "",
                        submittedAt: selectedSubmission.submittedAt
                      }}
                      showExecutionBlock={true}
                      templateVersion={selectedSubmission.templateVersion}
                    />
                  </div>
	                )}
	              </div>

	              <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
	                {candidatePendingQueue.length > 1 ? (
	                  <div className="rounded-[24px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
	                    {candidatePendingQueue.length} pending agreements for this candidate · Showing{" "}
	                    {currentQueueIndex >= 0 ? currentQueueIndex + 1 : 1} of{" "}
	                    {candidatePendingQueue.length}
                  </div>
                ) : null}

                {!isReviewable ? (
                  <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900">
                    This submission has already been reviewed:{" "}
                    <span className="font-semibold">
                      {selectedSubmission.approvalStatus === "approved" ? "Approved" : "Rejected"}
                    </span>
                    .
                  </div>
                ) : null}

                {modalError ? (
                  <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700">
                    {modalError}
	                  </div>
	                ) : null}

	                <label className="block space-y-2.5">
                  <span className="field-label">Feedback (visible to candidate)</span>
                  <textarea
                    className="input-field min-h-28"
                    onChange={(event) => setFeedback(event.target.value)}
                    placeholder="Add notes or required changes for the candidate."
                    value={feedback}
                  />
                </label>

                {isAddendum ? (
                  <div className="grid gap-4 rounded-[24px] border border-line/70 bg-white/70 p-5">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">
                      Authorized Signatory (admin only)
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="block space-y-2.5">
                        <span className="field-label">Authorized signatory name</span>
                        <input
                          className="input-field"
                          onChange={(event) => setAuthorizedSignatoryName(event.target.value)}
                          placeholder="Name"
                          value={authorizedSignatoryName}
                        />
                      </label>
                      <label className="block space-y-2.5">
                        <span className="field-label">Designation</span>
                        <input
                          className="input-field"
                          onChange={(event) => setAuthorizedSignatoryDesignation(event.target.value)}
                          placeholder="Designation"
                          value={authorizedSignatoryDesignation}
                        />
                      </label>
                    </div>
                    <label className="block space-y-2.5">
                      <span className="field-label">Employee name (as per addendum)</span>
                      <input
                        className="input-field"
                        onChange={(event) => setEmployeeName(event.target.value)}
                        placeholder={(selectedCandidate?.fullName ?? "Employee name") as string}
                        value={employeeName}
                      />
                    </label>
                    <p className="text-xs leading-6 text-slate">
                      These fields are added to the Addendum PDF at approval time and are not shown
                      to the candidate while filling the form.
                    </p>
                  </div>
	                ) : null}

	                <div className="sticky bottom-0 -mx-6 mt-6 border-t border-line/70 bg-white/95 px-6 py-4 backdrop-blur lg:static lg:mx-0 lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:backdrop-blur-0">
	                  {!isReviewable &&
	                  selectedSubmission.approvalStatus === "approved" &&
	                  selectedSubmission.pdfBase64 ? (
	                    <button
	                      className="btn-primary w-full justify-center px-4 py-2.5"
	                      onClick={() =>
	                        downloadPdfBase64({
	                          base64: selectedSubmission.pdfBase64!,
	                          fileName: downloadFileName
	                        })
	                      }
	                      type="button"
	                    >
	                      Download PDF
	                    </button>
	                  ) : null}

	                {isReviewable ? (
	                  <div className="mt-3 flex flex-col gap-3">
	                    <button
	                      className="btn-secondary w-full justify-center px-4 py-2.5"
	                      disabled={!selectedSubmissionId || isUpdating === selectedSubmissionId}
	                      onClick={() => {
                        if (!selectedSubmissionId) return;
                        setModalError(null);
                        void handleSubmissionDecision(
                          selectedSubmissionId,
                          "rejected",
                          feedback || "Rejected by administrator",
                          feedback || undefined
                        )
                          .then(async () => {
                            const nextPending = await refreshPendingAgreements();
                            await refreshApprovedAgreements();
                            const candidateId = selectedCandidateId;
                            const remainingQueue = candidateId
                              ? nextPending.filter((row) => row.candidate.id === candidateId)
                              : [];
                            const nextToOpen = remainingQueue[0] ?? null;
                            if (nextToOpen) {
                              await selectSubmission(nextToOpen.id);
                            } else {
                              setIsPreviewExpanded(false);
                            }
                          })
                          .catch((error) => {
                            setModalError(
                              error instanceof Error ? error.message : "Unable to reject agreement."
                            );
                          });
                      }}
                      type="button"
                    >
                      {selectedSubmissionId && isUpdating === selectedSubmissionId ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
	                      Reject
	                    </button>
	                    <button
	                      className="btn-primary w-full justify-center px-4 py-2.5"
	                      disabled={!selectedSubmissionId || isUpdating === selectedSubmissionId}
                      onClick={() => {
                        if (!selectedSubmissionId) return;
                        setModalError(null);
                        void handleSubmissionDecision(
                          selectedSubmissionId,
                          "approved",
                          undefined,
                          feedback || undefined,
                          isAddendum
                            ? {
                                authorizedSignatoryName,
                                authorizedSignatoryDesignation,
                                employeeName
                              }
                            : undefined
                        )
                          .then(async () => {
                            const nextPending = await refreshPendingAgreements();
                            await refreshApprovedAgreements();
                            const candidateId = selectedCandidateId;
                            const remainingQueue = candidateId
                              ? nextPending.filter((row) => row.candidate.id === candidateId)
                              : [];
                            const nextToOpen = remainingQueue[0] ?? null;
                            if (nextToOpen) {
                              await selectSubmission(nextToOpen.id);
                            } else {
                              setIsPreviewExpanded(false);
                            }
                          })
                          .catch((error) => {
                            setModalError(
                              error instanceof Error ? error.message : "Unable to approve agreement."
                            );
                          });
                      }}
                      type="button"
                    >
                      {selectedSubmissionId && isUpdating === selectedSubmissionId ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
	                      Approve
	                    </button>
	                  </div>
	                ) : null}
	                </div>
	              </div>
	            </div>
	            </div>
	          </div>
	        </div>
	      ) : null}

      <section className="surface p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow">Agreements</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
              Approved agreements
            </h2>
            <p className="mt-2 text-sm leading-7 text-slate">
              Recently approved submissions. Click a row to preview/download.
            </p>
          </div>
          <button
            className="btn-secondary"
            onClick={() => void refreshApprovedAgreements()}
            type="button"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {approvedError ? (
          <div className="mt-5 rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700">
            {approvedError}
          </div>
        ) : null}

        <div className="mt-5 grid gap-3 rounded-[24px] border border-line/70 bg-white/70 p-4 sm:grid-cols-2">
          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
              Filter by college
            </span>
            <select
              className="input-field"
              onChange={(event) => setApprovedCollegeFilter(event.target.value)}
              value={approvedCollegeFilter}
            >
              {approvedCollegeOptions.map((option) => (
                <option key={option} value={option}>
                  {option === "all" ? "All colleges" : option}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
              Search
            </span>
            <input
              className="input-field"
              onChange={(event) => setApprovedSearch(event.target.value)}
              placeholder="Search by candidate, email, serial..."
              value={approvedSearch}
            />
          </label>
        </div>

        <div className="mt-5 overflow-hidden rounded-[24px] border border-line/70 bg-white/70">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
	              <thead className="bg-cloud/40">
	                <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
	                  <th className="px-4 py-3">Candidate</th>
	                  <th className="px-4 py-3">College</th>
	                  <th className="px-4 py-3">Internship Agreement</th>
	                  <th className="px-4 py-3">Addendum Agreement</th>
	                  <th className="px-4 py-3">Approved</th>
	                  <th className="px-4 py-3">Status</th>
	                </tr>
              </thead>
              <tbody className="divide-y divide-line/70">
                {filteredApprovedGroups.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-sm text-slate" colSpan={6}>
                      <EmptyState text="No approved agreements yet." />
                    </td>
                  </tr>
                ) : (
                  filteredApprovedGroups.slice(0, 50).map((group) => (
                    <tr className="transition hover:bg-cloud/30" key={group.candidate.id}>
                      <td className="px-4 py-4">
                        <p className="text-sm font-semibold text-ink">{group.candidate.fullName}</p>
                        <p className="text-sm text-slate">{group.candidate.email}</p>
                      </td>
	                      <td className="px-4 py-4 text-sm text-slate">
	                        {group.candidate.university || "—"}
	                      </td>
	                      <td className="px-4 py-4">{renderAgreementCell(group.internship)}</td>
	                      <td className="px-4 py-4">{renderAgreementCell(group.addendum)}</td>
	                      <td className="px-4 py-4 text-sm text-slate">
	                        {group.latestApprovedAt ? formatDisplayDate(group.latestApprovedAt) : "—"}
	                      </td>
	                      <td className="px-4 py-4">
	                        <StatusPill status="approved" />
	                      </td>
	                    </tr>
	                  ))
	                )}
	              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

export function AdminVerifiedApprovalsPage() {
  const {
    approvedUsers,
    handleDeleteCandidate,
    handleUpdateCandidate,
    isDeletingCandidateId,
    isUpdating
  } = useAdminWorkspace();
  const [editingCandidate, setEditingCandidate] = useState<DashboardResponse["users"][number] | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 8;
  const totalPages = Math.max(1, Math.ceil(approvedUsers.length / pageSize));
  const currentPageIndex = Math.min(pageIndex, totalPages - 1);
  const pageStart = currentPageIndex * pageSize;
  const visibleApprovedUsers = approvedUsers.slice(pageStart, pageStart + pageSize);
  const resultStart = approvedUsers.length === 0 ? 0 : pageStart + 1;
  const resultEnd = Math.min(pageStart + visibleApprovedUsers.length, approvedUsers.length);

  return (
    <div className="mt-6">
      <section className="surface p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-cloud text-royal">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="eyebrow">Verified Approvals</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
                Approved candidates ready for onboarding
              </h2>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
                Review approved candidate records and verification status in one concise table.
              </p>
            </div>
          </div>

          <div className="rounded-[18px] border border-line/70 bg-cloud/55 px-4 py-3 text-sm font-medium text-slate">
            {approvedUsers.length} approved users
          </div>
        </div>

        <div
          className="mt-6 overflow-hidden rounded-[24px] border border-line/80 bg-white shadow-sm"
          data-testid="admin-verified-approvals-table"
        >
          {approvedUsers.length === 0 ? (
            <EmptyState text="No approved candidates are available yet." />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-line/80 bg-cloud/70 text-xs font-semibold uppercase tracking-[0.12em] text-slate/70">
                    <tr>
                      <th className="min-w-[320px] px-5 py-4">Candidate</th>
                      <th className="min-w-[150px] px-5 py-4">Status</th>
                      <th className="min-w-[190px] px-5 py-4">Email verification</th>
                      <th className="min-w-[170px] px-5 py-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line/70">
                    {visibleApprovedUsers.map((candidate) => (
                      <tr
                        className="transition hover:bg-cloud/45"
                        data-testid={`admin-verified-approvals-row-${candidate.id}`}
                        key={candidate.id}
                      >
                        <td className="px-5 py-4 align-top">
                          <p className="font-semibold text-ink">{candidate.fullName}</p>
                          <p className="mt-1 text-sm font-medium text-slate">{candidate.email}</p>
                          <p className="mt-1 text-sm text-slate">{candidate.phone || "No phone added"}</p>
                        </td>
                        <td className="px-5 py-4 align-top">
                          <StatusPill status={candidate.status} />
                        </td>
                        <td className="px-5 py-4 align-top text-sm text-slate">
                          {candidate.emailVerifiedAt
                            ? formatDisplayDate(candidate.emailVerifiedAt)
                            : "Not verified"}
                        </td>
                        <td className="px-5 py-4 align-top">
                          <CandidateRowActions
                            candidate={candidate}
                            isDeleting={isDeletingCandidateId === candidate.id}
                            isSaving={isUpdating === candidate.id}
                            onDelete={handleDeleteCandidate}
                            onEdit={setEditingCandidate}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-3 border-t border-line/80 bg-cloud/35 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate" data-testid="admin-verified-approvals-pagination-summary">
                  Showing {resultStart}-{resultEnd} of {approvedUsers.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="btn-secondary px-3 py-2 text-xs"
                    data-testid="admin-verified-approvals-pagination-prev"
                    disabled={currentPageIndex === 0}
                    onClick={() => setPageIndex((current) => Math.max(0, current - 1))}
                    type="button"
                  >
                    Previous
                  </button>
                  <span
                    className="rounded-xl border border-line/70 bg-white px-3 py-2 text-xs font-semibold text-ink"
                    data-testid="admin-verified-approvals-pagination-page"
                  >
                    Page {currentPageIndex + 1} of {totalPages}
                  </span>
                  <button
                    className="btn-secondary px-3 py-2 text-xs"
                    data-testid="admin-verified-approvals-pagination-next"
                    disabled={currentPageIndex >= totalPages - 1}
                    onClick={() => setPageIndex((current) => Math.min(totalPages - 1, current + 1))}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {editingCandidate ? (
          <CandidateEditDialog
            candidate={editingCandidate}
            isSaving={isUpdating === editingCandidate.id}
            onClose={() => setEditingCandidate(null)}
            onSave={async (payload) => {
              await handleUpdateCandidate(editingCandidate.id, payload);
              setEditingCandidate(null);
            }}
          />
        ) : null}
      </section>
    </div>
  );
}

export function AdminAllFormsPage({
  initialFilter = "all"
}: {
  initialFilter?: "all" | TemplateVersion["status"];
}) {
  const {
    dashboard,
    draftTemplateVersions,
    handleDeleteTemplateVersion,
    isDeletingTemplateVersionId,
    publishedTemplateVersions
  } = useAdminWorkspace();
  const [formFilter, setFormFilter] = useState<"all" | TemplateVersion["status"]>(initialFilter);
  const [searchValue, setSearchValue] = useState("");

  const templateVersions = dashboard.template.versions ?? [];
  const archivedTemplateVersions = templateVersions.filter((version) => version.status === "archived");
  const activeVersionId = dashboard.template.activeVersion.id;
  const totalSubmittedAgreements = useMemo(
    () => templateVersions.reduce((sum, version) => sum + (version.submittedCount ?? 0), 0),
    [templateVersions]
  );
  const sortedTemplateVersions = useMemo(
    () => [...templateVersions].sort((left, right) => right.versionNumber - left.versionNumber),
    [templateVersions]
  );
  const visibleTemplateVersions = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();

    return sortedTemplateVersions.filter((version) => {
      if (formFilter !== "all" && version.status !== formFilter) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const searchableText = [
        version.title,
        version.description ?? "",
        version.status,
        `v${version.versionNumber}`
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });
  }, [formFilter, searchValue, sortedTemplateVersions]);
  const filterOptions: Array<{
    count: number;
    label: string;
    value: "all" | TemplateVersion["status"];
  }> = [
    { count: templateVersions.length, label: "All", value: "all" },
    { count: draftTemplateVersions.length, label: "Drafts", value: "draft" },
    { count: publishedTemplateVersions.length, label: "Published", value: "published" },
    { count: archivedTemplateVersions.length, label: "Archived", value: "archived" }
  ];

  return (
    <div className="mt-6 space-y-6">
      <section className="surface p-6 md:p-7">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="eyebrow">{initialFilter === "draft" ? "Saved Drafts" : "All Forms"}</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
              {initialFilter === "draft" ? "Draft form library" : "Form library"}
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
              {initialFilter === "draft"
                ? "Browse saved drafts, open one to continue editing, or remove old versions."
                : "Browse saved forms, search quickly, and open the document you want to review."}
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard label="Forms Created" value={`${templateVersions.length}`} />
          <SummaryCard label="Draft Forms" value={`${draftTemplateVersions.length}`} />
          <SummaryCard label="Published Forms" value={`${publishedTemplateVersions.length}`} />
          <SummaryCard label="Submitted Agreements" value={`${totalSubmittedAgreements}`} />
        </div>
      </section>

      <section className="surface p-6" id="saved-forms">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="eyebrow">{initialFilter === "draft" ? "Draft Forms" : "Recent Forms"}</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
              {initialFilter === "draft" ? "Browse saved drafts" : "Browse saved forms"}
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
              {initialFilter === "draft"
                ? "Find a draft quickly, then open it to keep editing or publish it later."
                : "Find a draft or published form quickly, then open it to review, edit, or remove it."}
            </p>
          </div>

          <label className="block w-full max-w-md">
            <span className="sr-only">Search forms</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/70" />
              <input
                className="input-field pl-11"
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search by form title or status"
                type="search"
                value={searchValue}
              />
            </div>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {filterOptions.map((option) => (
            <button
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                formFilter === option.value
                  ? "border-navy bg-navy text-white shadow-sm"
                  : "border-line bg-cloud/60 text-slate hover:border-royal/20 hover:bg-white hover:text-ink"
              }`}
              key={option.value}
              onClick={() => setFormFilter(option.value)}
              type="button"
            >
              {option.label}
              <span className="ml-2 text-xs opacity-80">{option.count}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {visibleTemplateVersions.length === 0 ? (
            <div className="sm:col-span-2 xl:col-span-3">
              <EmptyState
                text={
                  templateVersions.length === 0
                    ? "No forms have been created yet."
                    : "No forms match the current search or filter."
                }
              />
            </div>
          ) : (
            visibleTemplateVersions.map((version) => (
              <TemplateVersionCard
                activeVersionId={activeVersionId}
                deletingVersionId={isDeletingTemplateVersionId}
                key={version.id}
                onDelete={handleDeleteTemplateVersion}
                version={version}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

export function AdminCreateFormPage() {
  const {
    approvedUsers,
    dashboard,
    draftTemplateVersions,
    handleCreateTemplateDraft,
    handlePublishTemplate,
    isPublishingTemplate,
    isSavingTemplate,
    setTemplateForm,
    setTemplateVersionIdToPublish,
    templateForm,
    templateVersionIdToPublish
  } = useAdminWorkspace();
  const [activeStep, setActiveStep] = useState<1 | 2 | 3>(1);
  const [pdfAnalysis, setPdfAnalysis] = useState<PdfAnalysisResult | null>(null);
  const [isAnalyzingPdf, setIsAnalyzingPdf] = useState(false);
  const [pdfAnalysisError, setPdfAnalysisError] = useState<string | null>(null);
  const [publishScope, setPublishScope] = useState<"all_approved_users" | "selected_users">(
    "all_approved_users"
  );
  const [publishSearchValue, setPublishSearchValue] = useState("");
  const [publishFilterUniversity, setPublishFilterUniversity] = useState("");
  const [publishFilterCity, setPublishFilterCity] = useState("");
  const [selectedPublishCandidateIds, setSelectedPublishCandidateIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedPublishCandidateIds((current) =>
      current.filter((candidateId) => approvedUsers.some((user) => user.id === candidateId))
    );
  }, [approvedUsers]);

  const uniquePublishUniversities = useMemo(() => {
    const values = approvedUsers.map((u) => u.university).filter((v) => v.trim().length > 0);
    return Array.from(new Set(values)).sort();
  }, [approvedUsers]);

  const uniquePublishCities = useMemo(() => {
    const values = approvedUsers.map((u) => u.city).filter((v) => v.trim().length > 0);
    return Array.from(new Set(values)).sort();
  }, [approvedUsers]);

  const filteredApprovedUsers = useMemo(() => {
    const normalizedQuery = publishSearchValue.trim().toLowerCase();

    return approvedUsers.filter((user) => {
      if (normalizedQuery && ![user.fullName, user.email].join(" ").toLowerCase().includes(normalizedQuery)) {
        return false;
      }
      if (publishFilterUniversity && user.university !== publishFilterUniversity) {
        return false;
      }
      if (publishFilterCity && user.city !== publishFilterCity) {
        return false;
      }
      return true;
    });
  }, [approvedUsers, publishSearchValue, publishFilterUniversity, publishFilterCity]);
  const selectedPublishUsers = useMemo(
    () => approvedUsers.filter((user) => selectedPublishCandidateIds.includes(user.id)),
    [approvedUsers, selectedPublishCandidateIds]
  );
  const selectedVersionToPublish = useMemo(
    () => draftTemplateVersions.find((version) => version.id === templateVersionIdToPublish) ?? null,
    [draftTemplateVersions, templateVersionIdToPublish]
  );
  const canPublishSelectedUsers =
    Boolean(selectedVersionToPublish) &&
    (publishScope === "selected_users" ? selectedPublishCandidateIds.length > 0 : true);
  const areAllVisibleUsersSelected =
    filteredApprovedUsers.length > 0 &&
    filteredApprovedUsers.every((user) => selectedPublishCandidateIds.includes(user.id));
  const selectedPreviewUsers = selectedPublishUsers.slice(0, 4);
  const additionalSelectedUsersCount = Math.max(
    0,
    selectedPublishUsers.length - selectedPreviewUsers.length
  );
  const publishAudienceSummary =
    !selectedVersionToPublish
      ? "Save a draft first. Publishing only unlocks after at least one draft exists."
      : publishScope === "all_approved_users"
      ? `This version becomes the default form for ${approvedUsers.length} approved users.`
      : canPublishSelectedUsers
        ? `This version will be assigned to ${formatAssignedCandidateLabel(selectedPublishCandidateIds.length)} while everyone else keeps the current default form.`
        : "Select at least one approved user to publish this form to a targeted audience.";

  const previewValues = useMemo(
    () =>
      buildAgreementFormDefaults({
        schema: templateForm.formSchema,
        source: {},
        templateDefaults: {
          companyAddress: templateForm.companyAddress,
          confidentialityPeriod: templateForm.defaultConfidentialityPeriod,
          jurisdictionCity: templateForm.defaultJurisdictionCity,
          workingHours: templateForm.defaultWorkingHours
        },
        user: {
          city: "Mysore, Karnataka",
          fullName: "Preview Intern"
        }
      }),
    [templateForm]
  );
  const previewAgreement = useMemo(
    () => buildFormPreviewAgreement(templateForm, previewValues),
    [templateForm, previewValues]
  );
  const previewTemplateVersion = useMemo(
    () => buildPreviewTemplateVersion(templateForm, dashboard.template.activeVersion),
    [dashboard.template.activeVersion, templateForm]
  );
  const schemaFieldCount = countSchemaFieldBlocks(templateForm.formSchema);
  const documentReady = hasDocumentContent(templateForm);

  function updateTemplateField<Key extends EditableTemplateField>(
    field: Key,
    value: AdminTemplateForm[Key]
  ) {
    setTemplateForm((current) =>
      current
        ? {
            ...current,
            [field]: value,
            formSchema:
              field === "title"
                ? { ...current.formSchema, title: String(value) }
                : field === "description"
                  ? { ...current.formSchema, description: String(value) || undefined }
                  : current.formSchema
          }
        : current
    );
  }

  function updateTemplateSchema(schema: AdminTemplateForm["formSchema"]) {
    setTemplateForm((current) =>
      current
        ? {
            ...current,
            title: schema.title,
            description: schema.description ?? "",
            formSchema: schema
          }
        : current
    );
  }

  function togglePublishCandidate(candidateId: string) {
    setSelectedPublishCandidateIds((current) =>
      current.includes(candidateId)
        ? current.filter((value) => value !== candidateId)
        : [...current, candidateId]
    );
  }

  function updateVisibleCandidateSelection(shouldSelect: boolean) {
    const visibleCandidateIds = filteredApprovedUsers.map((user) => user.id);

    setSelectedPublishCandidateIds((current) => {
      if (shouldSelect) {
        return Array.from(new Set([...current, ...visibleCandidateIds]));
      }

      return current.filter((candidateId) => !visibleCandidateIds.includes(candidateId));
    });
  }

  async function handlePdfUpload(file: File) {
    setPdfAnalysisError(null);
    setPdfAnalysis(null);
    setIsAnalyzingPdf(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip the data URL prefix, keep only the base64 part
          resolve(result.split(",")[1] ?? result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await analyzePdfForFields(base64);
      setPdfAnalysis(result);
    } catch (err) {
      setPdfAnalysisError(err instanceof Error ? err.message : "Failed to analyze PDF.");
    } finally {
      setIsAnalyzingPdf(false);
    }
  }

  function applyPdfSuggestions(detectedSections: PdfDetectedSection[]) {
    const newSections = detectedSections
      .filter((s) => s.fields.length > 0)
      .map((section, sIdx) => {
        const blocks = section.fields.map((field) => {
          const blockType = field.type === "dropdown" ? "dropdown" : field.type;
          const base = createEmptyBlock(blockType);
          if ("fieldKey" in base) {
            (base as any).fieldKey = field.fieldKey;
            (base as any).label = field.label;
            (base as any).required = field.required;
            if ("placeholder" in base) {
              (base as any).placeholder = `Enter ${field.label.toLowerCase()}`;
            }
          }
          return base;
        });
        return {
          id: `pdf-section-${sIdx}-${Math.random().toString(36).slice(2, 7)}`,
          title: section.title,
          description: "",
          blocks
        };
      });

    const signatureSection = detectedSections.find(
      (s) => s.fields.length === 0 && /signature|declaration/i.test(s.title)
    );
    const sigBlock = createEmptyBlock("signature");
    const sigSection = {
      id: `pdf-sig-${Math.random().toString(36).slice(2, 7)}`,
      title: signatureSection?.title ?? "Signature & Declaration",
      description: "",
      blocks: [sigBlock]
    };

    setTemplateForm((current) => {
      if (!current) return current;
      return {
        ...current,
        formSchema: {
          ...current.formSchema,
          sections: [...newSections, sigSection]
        }
      };
    });
    setPdfAnalysis(null);
  }

  return (
    <div className="mt-6 space-y-6">

      {/* ── Step navigation ── */}
      <section className="surface p-6 md:p-7">
        <p className="eyebrow">Create Form</p>
        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
          Create a new agreement form
        </h2>
        <p className="mt-2 text-sm text-slate">
          Complete all three steps in order, then send the form to candidates.
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {(
            [
              {
                num: 1 as const,
                title: "Candidate Questions",
                status: schemaFieldCount > 0 ? `${schemaFieldCount} fields added` : "Required — add fields",
                done: schemaFieldCount > 0
              },
              {
                num: 2 as const,
                title: "Agreement & PDF",
                status: documentReady ? "Content added" : "Required — add content",
                done: documentReady
              },
              {
                num: 3 as const,
                title: "Save & Send",
                status: draftTemplateVersions.length > 0
                  ? `${draftTemplateVersions.length} draft${draftTemplateVersions.length === 1 ? "" : "s"} saved`
                  : "Save a draft, then send",
                done: false
              }
            ] as const
          ).map(({ num, title, status, done }) => (
            <button
              className={cn(
                "rounded-[22px] border px-4 py-4 text-left transition",
                activeStep === num
                  ? "border-navy bg-navy shadow-lg shadow-navy/10"
                  : "border-line/80 bg-white hover:border-royal/20 hover:bg-cloud/35"
              )}
              key={num}
              onClick={() => setActiveStep(num)}
              type="button"
            >
              <div className="flex items-center gap-3">
                <span className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                  activeStep === num ? "bg-white/20 text-white" : "bg-navy text-white"
                )}>
                  {num}
                </span>
                <div className="min-w-0">
                  <p className={cn("text-sm font-semibold", activeStep === num ? "text-white" : "text-ink")}>
                    {title}
                  </p>
                  <p className={cn(
                    "mt-0.5 text-xs",
                    activeStep === num
                      ? "text-white/70"
                      : done
                        ? "text-emerald-600"
                        : num < 3 ? "text-amber-600" : "text-slate/70"
                  )}>
                    {status}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Step 1: Candidate Questions ── */}
      {activeStep === 1 && (
        <section className="surface p-6 md:p-7">
          <p className="eyebrow">Step 1 of 3</p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
            Build candidate questions
          </h3>
          <p className="mt-2 text-sm leading-7 text-slate">
            Add the input fields candidates must fill in. Or{" "}
            <strong>upload your existing agreement PDF</strong> to auto-detect the fields.
          </p>

          {/* ── PDF Auto-detect panel ── */}
          <div className="mt-5 rounded-[22px] border border-royal/25 bg-gradient-to-br from-blue-50/60 to-white p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-royal" />
                  <p className="text-sm font-semibold text-ink">Auto-detect fields from PDF</p>
                </div>
                <p className="mt-1 max-w-lg text-sm text-slate">
                  Upload your existing agreement PDF. The system reads it and automatically
                  generates the form fields candidates need to fill in.
                </p>
              </div>
              <label className="shrink-0">
                <span className="btn-secondary cursor-pointer">
                  {isAnalyzingPdf ? (
                    <><LoaderCircle className="h-4 w-4 animate-spin" /> Analyzing…</>
                  ) : (
                    <><FileText className="h-4 w-4" /> Upload PDF</>
                  )}
                </span>
                <input
                  accept=".pdf,application/pdf"
                  className="sr-only"
                  disabled={isAnalyzingPdf}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handlePdfUpload(file);
                    e.target.value = "";
                  }}
                  type="file"
                />
              </label>
            </div>

            {pdfAnalysisError ? (
              <div className="mt-4 rounded-[16px] border border-red-200 bg-red-50/60 px-4 py-3 text-sm text-red-800">
                {pdfAnalysisError}
              </div>
            ) : null}

            {pdfAnalysis ? (
              <div className="mt-4 rounded-[20px] border border-royal/20 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-ink">
                      {pdfAnalysis.totalFields} field{pdfAnalysis.totalFields === 1 ? "" : "s"} detected
                      {" "}across {pdfAnalysis.sections.filter((s) => s.fields.length > 0).length} section{pdfAnalysis.sections.filter((s) => s.fields.length > 0).length === 1 ? "" : "s"}
                    </p>
                    <p className="mt-1 text-xs text-slate">
                      Review the detected fields below, then apply them to the form builder.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="btn-ghost px-3 py-2 text-xs"
                      onClick={() => setPdfAnalysis(null)}
                      type="button"
                    >
                      Dismiss
                    </button>
                    <button
                      className="btn-primary px-4 py-2 text-sm"
                      onClick={() => applyPdfSuggestions(pdfAnalysis.sections)}
                      type="button"
                    >
                      Apply to form builder
                    </button>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {pdfAnalysis.sections.map((section, sIdx) => (
                    <div className="rounded-[16px] border border-line/70 bg-cloud/25 p-3" key={sIdx}>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate/70">
                        {section.title}
                      </p>
                      {section.fields.length === 0 ? (
                        <p className="mt-1 text-xs text-slate">Signature block will be added here.</p>
                      ) : (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {section.fields.map((field) => (
                            <span
                              className={cn(
                                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                                field.isReserved
                                  ? "border-royal/30 bg-blue-50 text-royal"
                                  : "border-line/70 bg-white text-ink"
                              )}
                              key={field.fieldKey}
                            >
                              <span className="text-[10px] uppercase tracking-wide text-slate/60">{field.type}</span>
                              {field.label}
                              {field.isReserved ? (
                                <span className="rounded-full bg-royal/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-royal">
                                  mapped
                                </span>
                              ) : null}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-6">
            <AgreementFormStudio
              onChange={updateTemplateSchema}
              previewValues={previewValues}
              schema={templateForm.formSchema}
            />
          </div>

          <div className="mt-6 flex justify-end">
            <button className="btn-primary" onClick={() => setActiveStep(2)} type="button">
              Next: Agreement &amp; PDF →
            </button>
          </div>
        </section>
      )}

      {/* ── Step 2: Agreement & PDF ── */}
      {activeStep === 2 && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(360px,0.95fr)]">
          <section className="surface p-6 md:p-7">
            <p className="eyebrow">Step 2 of 3</p>
            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
              Write the agreement &amp; PDF content
            </h3>
            <p className="mt-2 text-sm leading-7 text-slate">
              Fill in the legal text and company details. This is what candidates read and sign.
            </p>

            <div className="mt-4 rounded-[16px] border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-900">
              <strong>Required:</strong> Title · Company name · Agreement body · Terms &amp; conditions · Data protection notice
            </div>

            <FormEditorFields onChange={updateTemplateField} templateForm={templateForm} />

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <button className="btn-ghost" onClick={() => setActiveStep(1)} type="button">
                ← Back to Questions
              </button>
              <button className="btn-primary" onClick={() => setActiveStep(3)} type="button">
                Next: Save &amp; Send →
              </button>
            </div>
          </section>

          <DocumentPreviewPanel
            agreement={previewAgreement}
            candidate={{ email: "ganesh.sabale@example.com", fullName: "Ganesh Sabale", phone: "+91 9876543210" }}
            description="Live preview of the agreement as candidates will see it."
            templateVersion={previewTemplateVersion}
            title="Live Preview"
          />
        </div>
      )}

      {/* ── Step 3: Save & Send ── */}
      {activeStep === 3 && (
        <section className="surface p-6 md:p-7">
          <p className="eyebrow">Step 3 of 3</p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
            Save &amp; send the form
          </h3>
          <p className="mt-2 text-sm leading-7 text-slate">
            Save a draft first, then choose who receives this form and send it.
          </p>

          {/* Readiness checklist */}
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className={cn(
              "flex items-center gap-3 rounded-[18px] border px-4 py-3",
              schemaFieldCount > 0 ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"
            )}>
              <span className={cn("text-lg font-bold", schemaFieldCount > 0 ? "text-emerald-600" : "text-amber-500")}>
                {schemaFieldCount > 0 ? "✓" : "!"}
              </span>
              <div>
                <p className="text-sm font-semibold text-ink">Candidate questions</p>
                <p className={cn("text-xs", schemaFieldCount > 0 ? "text-emerald-700" : "text-amber-700")}>
                  {schemaFieldCount > 0 ? `${schemaFieldCount} fields added` : "No fields — go back to Step 1"}
                </p>
              </div>
            </div>
            <div className={cn(
              "flex items-center gap-3 rounded-[18px] border px-4 py-3",
              documentReady ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"
            )}>
              <span className={cn("text-lg font-bold", documentReady ? "text-emerald-600" : "text-amber-500")}>
                {documentReady ? "✓" : "!"}
              </span>
              <div>
                <p className="text-sm font-semibold text-ink">Agreement content</p>
                <p className={cn("text-xs", documentReady ? "text-emerald-700" : "text-amber-700")}>
                  {documentReady ? "Content added" : "Missing — go back to Step 2"}
                </p>
              </div>
            </div>
          </div>

          {/* Save draft */}
          <div className="mt-6 rounded-[22px] border border-line/80 bg-cloud/25 p-5">
            <p className="text-sm font-semibold text-ink">1. Save as draft</p>
            <p className="mt-1 text-sm text-slate">
              Locks in your work without sending to candidates. You can edit again before publishing.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                className="btn-secondary"
                disabled={isSavingTemplate}
                onClick={() => void handleCreateTemplateDraft()}
                type="button"
              >
                {isSavingTemplate ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save draft
              </button>
              {draftTemplateVersions.length > 0 ? (
                <span className="text-sm text-slate">
                  {draftTemplateVersions.length} draft{draftTemplateVersions.length === 1 ? "" : "s"} saved
                </span>
              ) : null}
              <Link className="btn-ghost" to="/admin/saved-drafts">View all drafts</Link>
            </div>
          </div>

          {/* Send / publish */}
          <div className="mt-6">
            <p className="text-sm font-semibold text-ink">2. Choose who receives this form</p>
            <p className="mt-1 text-sm text-slate">
              Select the draft to publish, then pick the audience.
            </p>

            {draftTemplateVersions.length === 0 ? (
              <div className="mt-4 rounded-[20px] border border-dashed border-line bg-cloud/35 px-5 py-6 text-center text-sm text-slate">
                Save a draft first — the send options will appear here.
              </div>
            ) : (
              <div className="mt-4 space-y-5">

                {/* Draft picker */}
                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate/70">
                    Select draft to send
                  </span>
                  <select
                    className="input-field"
                    onChange={(e) => setTemplateVersionIdToPublish(e.target.value)}
                    value={templateVersionIdToPublish}
                  >
                    {draftTemplateVersions.map((version) => (
                      <option key={version.id} value={version.id}>
                        {getTemplateVersionOptionLabel(version)}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Audience options */}
                <div className="grid gap-3 md:grid-cols-2">
                  <button
                    className={cn(
                      "rounded-[22px] border px-4 py-4 text-left transition",
                      publishScope === "all_approved_users"
                        ? "border-navy bg-navy text-white shadow-lg shadow-navy/10"
                        : "border-line/80 bg-white text-ink hover:border-royal/20 hover:bg-cloud/35"
                    )}
                    onClick={() => setPublishScope("all_approved_users")}
                    type="button"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={cn("text-[11px] font-semibold uppercase tracking-[0.18em]", publishScope === "all_approved_users" ? "text-white/70" : "text-slate/70")}>
                          Send to everyone
                        </p>
                        <p className="mt-2 text-sm font-semibold">All approved candidates</p>
                      </div>
                      <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", publishScope === "all_approved_users" ? "bg-white/15 text-white" : "bg-cloud text-slate")}>
                        {approvedUsers.length} users
                      </span>
                    </div>
                    <p className={cn("mt-2 text-sm leading-6", publishScope === "all_approved_users" ? "text-white/85" : "text-slate")}>
                      This becomes the default form for every approved candidate.
                    </p>
                  </button>

                  <button
                    className={cn(
                      "rounded-[22px] border px-4 py-4 text-left transition",
                      publishScope === "selected_users"
                        ? "border-navy bg-navy text-white shadow-lg shadow-navy/10"
                        : "border-line/80 bg-white text-ink hover:border-royal/20 hover:bg-cloud/35"
                    )}
                    onClick={() => setPublishScope("selected_users")}
                    type="button"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={cn("text-[11px] font-semibold uppercase tracking-[0.18em]", publishScope === "selected_users" ? "text-white/70" : "text-slate/70")}>
                          Send selectively
                        </p>
                        <p className="mt-2 text-sm font-semibold">Specific candidates</p>
                      </div>
                      <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", publishScope === "selected_users" ? "bg-white/15 text-white" : "bg-emerald-50 text-emerald-700")}>
                        {selectedPublishCandidateIds.length} chosen
                      </span>
                    </div>
                    <p className={cn("mt-2 text-sm leading-6", publishScope === "selected_users" ? "text-white/85" : "text-slate")}>
                      Filter by college or city, then pick the candidates.
                    </p>
                  </button>
                </div>

                {/* Candidate picker */}
                {publishScope === "selected_users" ? (
                  <div className="rounded-[22px] border border-line/80 bg-white p-4 shadow-sm">
                    {approvedUsers.length === 0 ? (
                      <p className="px-4 py-6 text-center text-sm text-slate">No approved candidates yet.</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">Filter &amp; select candidates</p>
                          <div className="flex flex-wrap gap-2">
                            <span className="rounded-full border border-line/70 bg-white px-3 py-1.5 text-xs font-semibold text-slate/80">
                              {approvedUsers.length} approved
                            </span>
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                              {selectedPublishCandidateIds.length} selected
                            </span>
                          </div>
                        </div>

                        {selectedPublishUsers.length > 0 ? (
                          <div className="mt-3 rounded-[18px] border border-emerald-200 bg-emerald-50/60 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700/80">Selected</p>
                                <p className="mt-1 text-sm text-emerald-900">
                                  {formatAssignedCandidateLabel(selectedPublishCandidateIds.length)} will receive this form.
                                </p>
                              </div>
                              <button className="text-sm font-semibold text-emerald-800 hover:text-emerald-950" onClick={() => setSelectedPublishCandidateIds([])} type="button">
                                Clear all
                              </button>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {selectedPreviewUsers.map((user) => (
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-sm font-medium text-emerald-900" key={user.id}>
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-800">
                                    {getUserInitials(user.fullName)}
                                  </span>
                                  {user.fullName}
                                </span>
                              ))}
                              {additionalSelectedUsersCount > 0 ? (
                                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-sm text-emerald-900">
                                  +{additionalSelectedUsersCount} more
                                </span>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <label className="block">
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate/70">Filter by College / University</span>
                            <select className="input-field" onChange={(e) => setPublishFilterUniversity(e.target.value)} value={publishFilterUniversity}>
                              <option value="">All universities</option>
                              {uniquePublishUniversities.map((uni) => <option key={uni} value={uni}>{uni}</option>)}
                            </select>
                          </label>
                          <label className="block">
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate/70">Filter by City</span>
                            <select className="input-field" onChange={(e) => setPublishFilterCity(e.target.value)} value={publishFilterCity}>
                              <option value="">All cities</option>
                              {uniquePublishCities.map((city) => <option key={city} value={city}>{city}</option>)}
                            </select>
                          </label>
                        </div>

                        {(publishFilterUniversity || publishFilterCity) ? (
                          <div className="mt-2 flex items-center justify-between rounded-[14px] border border-royal/20 bg-blue-50/60 px-4 py-2 text-sm">
                            <span className="text-navy/80">
                              {filteredApprovedUsers.length} user{filteredApprovedUsers.length === 1 ? "" : "s"} match filters
                            </span>
                            <button className="text-xs font-semibold text-navy hover:text-royal" onClick={() => { setPublishFilterUniversity(""); setPublishFilterCity(""); }} type="button">
                              Clear filters
                            </button>
                          </div>
                        ) : null}

                        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="relative w-full max-w-xs">
                            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/70" />
                            <input
                              className="input-field pl-11"
                              onChange={(e) => setPublishSearchValue(e.target.value)}
                              placeholder="Search by name or email"
                              type="search"
                              value={publishSearchValue}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button className="btn-secondary px-3 py-2 text-xs" disabled={filteredApprovedUsers.length === 0} onClick={() => updateVisibleCandidateSelection(true)} type="button">
                              Select visible
                            </button>
                            <button className="btn-ghost px-3 py-2 text-xs" disabled={selectedPublishCandidateIds.length === 0} onClick={() => updateVisibleCandidateSelection(false)} type="button">
                              Clear visible
                            </button>
                          </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between rounded-[14px] border border-line/70 bg-cloud/30 px-4 py-2.5 text-sm text-slate">
                          <p>{filteredApprovedUsers.length} candidate{filteredApprovedUsers.length === 1 ? "" : "s"} shown</p>
                          <button className="text-sm font-semibold text-navy hover:text-royal" onClick={() => updateVisibleCandidateSelection(!areAllVisibleUsersSelected)} type="button">
                            {areAllVisibleUsersSelected ? "Unselect visible" : "Select all visible"}
                          </button>
                        </div>

                        <div className="mt-3 max-h-[280px] overflow-y-auto rounded-[18px] border border-line/80">
                          {filteredApprovedUsers.length === 0 ? (
                            <p className="px-4 py-8 text-center text-sm text-slate">No candidates match your filters.</p>
                          ) : (
                            filteredApprovedUsers.map((user) => {
                              const isSelected = selectedPublishCandidateIds.includes(user.id);
                              return (
                                <label className={cn("flex cursor-pointer items-center gap-3 border-b border-line/70 px-4 py-3 transition last:border-b-0", isSelected ? "bg-emerald-50/70" : "bg-white hover:bg-cloud/35")} key={user.id}>
                                  <input checked={isSelected} className="h-4 w-4 rounded border-line text-navy focus:ring-royal/20" onChange={() => togglePublishCandidate(user.id)} type="checkbox" />
                                  <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold", isSelected ? "bg-emerald-100 text-emerald-800" : "bg-cloud text-slate")}>
                                    {getUserInitials(user.fullName)}
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="flex flex-wrap items-center gap-2">
                                      <span className="block font-semibold text-ink">{user.fullName}</span>
                                      {isSelected ? <span className="rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">Included</span> : null}
                                    </span>
                                    <span className="block text-xs text-slate">{user.email}</span>
                                    {(user.university || user.city) ? (
                                      <span className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-slate/60">
                                        {user.university ? <span>{user.university}</span> : null}
                                        {user.city ? <span>{user.city}</span> : null}
                                      </span>
                                    ) : null}
                                  </span>
                                </label>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : null}

                {/* Send button */}
                <div className="flex flex-col gap-3 rounded-[22px] border border-line/80 bg-white/90 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-ink">Ready to send</p>
                    <p className="mt-1 max-w-2xl text-sm text-slate">{publishAudienceSummary}</p>
                  </div>
                  <button
                    className="btn-primary w-full sm:w-auto sm:min-w-[220px]"
                    disabled={isPublishingTemplate || !canPublishSelectedUsers}
                    onClick={() => void handlePublishTemplate({ publishScope, candidateIds: publishScope === "selected_users" ? selectedPublishCandidateIds : [] })}
                    type="button"
                  >
                    {isPublishingTemplate ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {publishScope === "all_approved_users" ? "Send to all approved users" : "Send to selected users"}
                  </button>
                </div>

              </div>
            )}
          </div>

          <div className="mt-4">
            <button className="btn-ghost" onClick={() => setActiveStep(2)} type="button">
              ← Back to Agreement
            </button>
          </div>
        </section>
      )}

    </div>
  );
}

export function AdminEditFormPage() {
  const [searchParams] = useSearchParams();
  const {
    handleCreateTemplateDraft,
    handleUpdateTemplateDraft,
    isSavingTemplate,
    selectedTemplateVersion,
    setTemplateForm,
    templateForm
  } = useAdminWorkspace();
  const isReadOnly = searchParams.get("mode") === "view";
  const isDraftVersion = selectedTemplateVersion.status === "draft";
  const [editorMode, setEditorMode] = useState<TemplateEditorMode>("builder");

  const previewValues = useMemo(
    () =>
      buildAgreementFormDefaults({
        schema: templateForm.formSchema,
        source: {},
        templateDefaults: {
          companyAddress: templateForm.companyAddress,
          confidentialityPeriod: templateForm.defaultConfidentialityPeriod,
          jurisdictionCity: templateForm.defaultJurisdictionCity,
          workingHours: templateForm.defaultWorkingHours
        },
        user: {
          city: "Mysore, Karnataka",
          fullName: "Preview Intern"
        }
      }),
    [templateForm]
  );
  const previewAgreement = useMemo(
    () => buildFormPreviewAgreement(templateForm, previewValues),
    [templateForm, previewValues]
  );
  const previewTemplateVersion = useMemo(
    () => buildPreviewTemplateVersion(templateForm, selectedTemplateVersion),
    [selectedTemplateVersion, templateForm]
  );
  const schemaSectionCount = templateForm.formSchema.sections.length;
  const schemaFieldCount = countSchemaFieldBlocks(templateForm.formSchema);

  function updateTemplateField<Key extends EditableTemplateField>(
    field: Key,
    value: AdminTemplateForm[Key]
  ) {
    if (isReadOnly) {
      return;
    }

    setTemplateForm((current) =>
      current
        ? {
            ...current,
            [field]: value,
            formSchema:
              field === "title"
                ? { ...current.formSchema, title: String(value) }
                : field === "description"
                  ? { ...current.formSchema, description: String(value) || undefined }
                  : current.formSchema
          }
        : current
    );
  }

  function updateTemplateSchema(schema: AdminTemplateForm["formSchema"]) {
    if (isReadOnly) {
      return;
    }

    setTemplateForm((current) =>
      current
        ? {
            ...current,
            title: schema.title,
            description: schema.description ?? "",
            formSchema: schema
          }
        : current
    );
  }

  return (
    <div className="mt-6">
      {isReadOnly ? (
        <div className="space-y-6">
          <section className="surface p-6 md:p-7">
            <p className="eyebrow">View Form</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
              Saved agreement preview
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
              Review the saved document as candidates will see it. This view is read-only.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <StatusPill status={selectedTemplateVersion.status} />
              <span className="text-sm text-slate">
                Published{" "}
                {selectedTemplateVersion.publishedAt
                  ? formatDisplayDate(selectedTemplateVersion.publishedAt)
                  : "Not published"}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                className="btn-secondary"
                to={buildTemplateVersionPath(selectedTemplateVersion.id, "edit")}
              >
                <SquarePen className="h-4 w-4" />
                Edit this form
              </Link>
              <Link className="btn-ghost" to="/admin/all-forms">
                Back to All Forms
              </Link>
            </div>
          </section>

          <DocumentPreviewPanel
            agreement={previewAgreement}
            candidate={{
              email: "ganesh.sabale@example.com",
              fullName: "Ganesh Sabale",
              phone: "+91 9876543210"
            }}
            description="This is the candidate-facing agreement preview for the saved form."
            templateVersion={previewTemplateVersion}
            title="Form Preview"
          />
        </div>
      ) : (
        <div className="space-y-6">
          <section className="surface p-6 md:p-7">
            <p className="eyebrow">Edit Form</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
              Production-ready form builder
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
              Refine the candidate-facing form schema and the legal template without losing the
              protections that keep saved drafts, approvals, and signed outputs stable.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3 md:justify-between">
              <StatusPill status={selectedTemplateVersion.status} />
              <div className="flex flex-wrap gap-3 text-sm text-slate">
                <span>{schemaSectionCount} sections</span>
                <span>{schemaFieldCount} fields</span>
              </div>
            </div>

            <div className="mt-6 rounded-[24px] border border-line/80 bg-cloud/35 p-2">
              <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
                Choose What You Want To Work On
              </p>
              <div className="flex flex-wrap gap-2">
                <EditorModeButton
                  active={editorMode === "document"}
                  description="Write the legal text, company details, and defaults used in the final preview and PDF."
                  label="2. Final agreement & PDF"
                  onClick={() => setEditorMode("document")}
                />
                <EditorModeButton
                  active={editorMode === "builder"}
                  description="Adjust sections, labels, dropdowns, dates, and signature fields for the candidate journey."
                  label="1. Candidate questions"
                  onClick={() => setEditorMode("builder")}
                />
              </div>
            </div>
          </section>

          {editorMode === "document" ? (
            <div className="grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(360px,0.95fr)]">
              <section className="surface p-6 md:p-7">
                <p className="eyebrow">Step 2</p>
                <h3 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
                  Write the final agreement
                </h3>
                <p className="mt-3 max-w-3xl text-sm leading-7 text-slate">
                  This is the legal document layer. Everything here flows into the signed agreement
                  preview and the PDF the candidate downloads.
                </p>

                <FormEditorFields
                  onChange={updateTemplateField}
                  templateForm={templateForm}
                />
              </section>

              <DocumentPreviewPanel
                agreement={previewAgreement}
                candidate={{
                  email: "ganesh.sabale@example.com",
                  fullName: "Ganesh Sabale",
                  phone: "+91 9876543210"
                }}
                description="This is the clean candidate preview for the agreement document."
                templateVersion={previewTemplateVersion}
                title="Live Preview"
              />
            </div>
          ) : (
            <section className="surface p-6 md:p-7">
              <p className="eyebrow">Step 1</p>
              <h3 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
                Build candidate questions
              </h3>
              <p className="mt-3 max-w-4xl text-sm leading-7 text-slate">
                Update the sections and input fields the candidate completes before signing the final agreement.
              </p>

              <div className="mt-6">
                <AgreementFormStudio
                  onChange={updateTemplateSchema}
                  previewValues={previewValues}
                  schema={templateForm.formSchema}
                />
              </div>
            </section>
          )}

          <section className="surface p-6 md:p-7">
            <div className="border-t border-line/80 pt-1">
              <p className="text-sm font-semibold text-ink">Save changes</p>
              <p className="mt-2 text-sm leading-7 text-slate">
                {isDraftVersion
                  ? "Update this draft directly when you finish editing."
                  : "Save these changes as a new draft before publishing them later."}
              </p>
              <button
                className="btn-secondary mt-5"
                disabled={isSavingTemplate}
                onClick={() =>
                  void (isDraftVersion
                    ? handleUpdateTemplateDraft(selectedTemplateVersion.id)
                    : handleCreateTemplateDraft())
                }
                type="button"
              >
                {isSavingTemplate ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isDraftVersion ? "Update draft" : "Save as new draft"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

type CandidateRow = DashboardResponse["users"][number];

function CandidateRowActions({
  candidate,
  isDeleting,
  isSaving,
  onDelete,
  onEdit
}: {
  candidate: CandidateRow;
  isDeleting: boolean;
  isSaving: boolean;
  onDelete: (candidateId: string) => Promise<void>;
  onEdit: (candidate: CandidateRow) => void;
}) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  async function handleConfirmDelete() {
    await onDelete(candidate.id);
    setIsDeleteDialogOpen(false);
  }

  return (
    <>
      <div className="flex flex-wrap justify-start gap-2 sm:justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={`Edit ${candidate.fullName}`}
              className={getIconActionButtonClassName()}
              data-testid={`admin-edit-user-${candidate.id}`}
              disabled={isDeleting || isSaving}
              onClick={() => onEdit(candidate)}
              type="button"
            >
              <SquarePen className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>Edit</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              aria-label={`Delete ${candidate.fullName}`}
              className={getIconActionButtonClassName("danger")}
              data-testid={`admin-delete-user-${candidate.id}`}
              disabled={isDeleting || isSaving}
              onClick={() => setIsDeleteDialogOpen(true)}
              type="button"
            >
              {isDeleting ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              <span className="sr-only">Delete</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>Delete</TooltipContent>
        </Tooltip>
      </div>

      <DeleteConfirmationDialog
        confirmLabel="Delete candidate"
        description={`This will permanently delete ${candidate.fullName}'s candidate account. This action cannot be undone.`}
        isDeleting={isDeleting}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        open={isDeleteDialogOpen}
        testId={`admin-delete-user-dialog-${candidate.id}`}
        title="Delete candidate account?"
      />
    </>
  );
}

function CandidateApprovalActions({
  candidate,
  isSaving,
  onDecision
}: {
  candidate: CandidateRow;
  isSaving: boolean;
  onDecision: (candidateId: string, decision: "approved" | "rejected") => Promise<void>;
}) {
  const [pendingDecision, setPendingDecision] = useState<"approved" | "rejected" | null>(null);
  const decisionCopy =
    pendingDecision === null
      ? null
      : getCandidateDecisionDialogCopy(candidate.fullName, pendingDecision);

  async function handleConfirmDecision() {
    if (!pendingDecision) {
      return;
    }

    await onDecision(candidate.id, pendingDecision);
    setPendingDecision(null);
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label={`Approve ${candidate.fullName}`}
            className={getIconActionButtonClassName("success")}
            data-testid={`admin-approve-user-${candidate.id}`}
            disabled={isSaving}
            onClick={() => setPendingDecision("approved")}
            type="button"
          >
            {isSaving && pendingDecision === "approved" ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            <span className="sr-only">Approve</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>Approve candidate</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label={`Reject ${candidate.fullName}`}
            className={getIconActionButtonClassName("danger")}
            data-testid={`admin-reject-user-${candidate.id}`}
            disabled={isSaving}
            onClick={() => setPendingDecision("rejected")}
            type="button"
          >
            {isSaving && pendingDecision === "rejected" ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            <span className="sr-only">Reject</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>Reject candidate</TooltipContent>
      </Tooltip>

      <AlertDialog
        onOpenChange={(open) => {
          if (!open && !isSaving) {
            setPendingDecision(null);
          }
        }}
        open={pendingDecision !== null}
      >
        <AlertDialogContent data-testid={`admin-candidate-decision-dialog-${candidate.id}`}>
          {decisionCopy ? (
            <>
              <div
                className={cn(
                  "mx-auto flex h-12 w-12 items-center justify-center rounded-full border",
                  pendingDecision === "approved"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-rose-200 bg-rose-50 text-rose-600"
                )}
              >
                {pendingDecision === "approved" ? (
                  <CheckCircle2 className="h-6 w-6" />
                ) : (
                  <XCircle className="h-6 w-6" />
                )}
              </div>

              <AlertDialogHeader>
                <AlertDialogTitle>{decisionCopy.title}</AlertDialogTitle>
                <AlertDialogDescription>{decisionCopy.description}</AlertDialogDescription>
              </AlertDialogHeader>

              <div
                className={cn(
                  "rounded-2xl border px-4 py-3",
                  pendingDecision === "approved"
                    ? "border-emerald-100 bg-emerald-50/80"
                    : "border-rose-100 bg-rose-50/80"
                )}
              >
                <p
                  className={cn(
                    "text-sm leading-6",
                    pendingDecision === "approved" ? "text-emerald-900" : "text-rose-800"
                  )}
                >
                  {decisionCopy.notice}
                </p>
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel
                  className="btn-secondary px-4 py-2.5"
                  data-testid={`admin-candidate-decision-cancel-${candidate.id}`}
                  disabled={isSaving}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className={cn(
                    "rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition",
                    "focus:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50",
                    pendingDecision === "approved"
                      ? "bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-100"
                      : "bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-100"
                  )}
                  data-testid={`admin-candidate-decision-confirm-${candidate.id}`}
                  disabled={isSaving}
                  onClick={(event) => {
                    event.preventDefault();
                    void handleConfirmDecision();
                  }}
                >
                  {isSaving ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : pendingDecision === "approved" ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  {decisionCopy.confirmLabel}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DeleteConfirmationDialog({
  confirmLabel,
  description,
  isDeleting,
  onClose,
  onConfirm,
  open,
  testId,
  title
}: {
  confirmLabel: string;
  description: string;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  open: boolean;
  testId: string;
  title: string;
}) {
  return (
    <ModalShell
      description={description}
      footer={
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            className="btn-secondary px-4 py-2.5"
            data-testid={`${testId}-cancel`}
            disabled={isDeleting}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn-danger px-4 py-2.5"
            data-testid={`${testId}-confirm`}
            disabled={isDeleting}
            onClick={() => void onConfirm()}
            type="button"
          >
            {isDeleting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {confirmLabel}
          </button>
        </div>
      }
      onClose={isDeleting ? () => undefined : onClose}
      open={open}
      title={title}
      widthClassName="max-w-lg"
    >
      <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3" data-testid={testId}>
        <p className="text-sm leading-6 text-rose-800">
          Deleting this record removes it from the admin tables and cannot be reversed.
        </p>
      </div>
    </ModalShell>
  );
}

function CandidateEditDialog({
  candidate,
  isSaving,
  onClose,
  onSave
}: {
  candidate: CandidateRow;
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: {
    email: string;
    fullName: string;
    phone?: string;
    status: CandidateRow["status"];
  }) => Promise<void>;
}) {
  const [form, setForm] = useState({
    email: candidate.email,
    fullName: candidate.fullName,
    phone: candidate.phone ?? "",
    status: candidate.status
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/35 px-4 py-6"
      data-testid="admin-candidate-edit-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-candidate-edit-title"
    >
      <form
        className="surface w-full max-w-xl p-6"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmedPhone = form.phone.trim();
          void onSave({
            email: form.email.trim(),
            fullName: form.fullName.trim(),
            phone: trimmedPhone.length > 0 ? trimmedPhone : undefined,
            status: form.status
          });
        }}
      >
        <p className="eyebrow">Candidate</p>
        <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink" id="admin-candidate-edit-title">
          Edit candidate details
        </h3>

        <div className="mt-6 grid gap-4">
          <TextField
            disabled={isSaving}
            label="Full name"
            onChange={(value) => setForm((current) => ({ ...current, fullName: value }))}
            value={form.fullName}
          />
          <label className="block space-y-2.5">
            <span className="field-label">Email</span>
            <input
              className="input-field"
              data-testid="admin-edit-user-email"
              disabled={isSaving}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              required
              type="email"
              value={form.email}
            />
          </label>
          <TextField
            disabled={isSaving}
            label="Phone"
            onChange={(value) => setForm((current) => ({ ...current, phone: value }))}
            value={form.phone}
          />
          <label className="block space-y-2.5">
            <span className="field-label">Status</span>
            <select
              className="input-field"
              data-testid="admin-edit-user-status"
              disabled={isSaving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  status: event.target.value as CandidateRow["status"]
                }))
              }
              value={form.status}
            >
              <option value="pending_email_verification">Pending email verification</option>
              <option value="pending_approval">Pending approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            className="btn-secondary px-4 py-2.5"
            disabled={isSaving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn-primary px-4 py-2.5"
            data-testid="admin-edit-user-submit"
            disabled={isSaving}
            type="submit"
          >
            {isSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}

export function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[22px] border border-line/70 bg-cloud/60 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">{label}</p>
      <p className="mt-2 break-all text-sm font-semibold text-ink">{value}</p>
    </div>
  );
}

export function EmptyState({ hint, text }: { hint?: string; text: string }) {
  return (
    <div className="rounded-[24px] border border-line/70 bg-cloud/55 px-5 py-6">
      <p className="text-sm leading-7 text-slate">{text}</p>
      {hint ? <p className="mt-1 text-xs text-slate/65">{hint}</p> : null}
    </div>
  );
}

export function UserOverviewCard({
  meta,
  subtitle,
  user
}: {
  meta: string;
  subtitle: string;
  user: DashboardResponse["users"][number];
}) {
  return (
    <div className="rounded-[24px] border border-line/70 bg-cloud/60 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-ink">{user.fullName}</p>
          <p className="mt-2 text-sm leading-7 text-slate">{subtitle}</p>
        </div>
        <StatusPill status={user.status} />
      </div>
      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
        {user.latestApprovalDecision
          ? `Latest decision ${user.latestApprovalDecision}`
          : "Awaiting latest review signal"}
      </p>
      <p className="mt-3 text-sm leading-6 text-slate">{meta}</p>
    </div>
  );
}

export function TextField({
  disabled = false,
  label,
  onChange,
  textarea = false,
  value
}: {
  disabled?: boolean;
  label: string;
  onChange: (value: string) => void;
  textarea?: boolean;
  value: string;
}) {
  return (
    <label className="block space-y-2.5">
      <span className="field-label">{label}</span>
      {textarea ? (
        <textarea
          className="input-field min-h-32"
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          value={value}
        />
      ) : (
        <input
          className="input-field"
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          value={value}
        />
      )}
    </label>
  );
}

function TextAreaField({
  disabled = false,
  label,
  minHeightClassName = "min-h-28",
  onChange,
  value
}: {
  disabled?: boolean;
  label: string;
  minHeightClassName?: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="block space-y-2.5">
      <span className="field-label">{label}</span>
      <textarea
        className={cn("input-field", minHeightClassName)}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      />
    </label>
  );
}

function FormEditorFields({
  onChange,
  templateForm
}: {
  onChange: (field: EditableTemplateField, value: string) => void;
  templateForm: AdminTemplateForm;
}) {
  const isAddendumTemplate = templateForm.title.toLowerCase().includes("addendum");

  return (
    <div className="mt-8 space-y-8">
      <EditorSection
        description="Keep the top of the document clear and formal so candidates understand what they are opening."
        title="Document basics"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <TextField
              label="Document title"
              onChange={(value) => onChange("title", value)}
              value={templateForm.title}
            />
          </div>
          <div className="md:col-span-2">
            <TextAreaField
              label="Short description"
              onChange={(value) => onChange("description", value)}
              value={templateForm.description}
            />
          </div>
          <TextField
            label="Company name"
            onChange={(value) => onChange("companyName", value)}
            value={templateForm.companyName}
          />
          {isAddendumTemplate ? null : (
            <TextAreaField
              label="Company address"
              onChange={(value) => onChange("companyAddress", value)}
              value={templateForm.companyAddress}
            />
          )}
        </div>
      </EditorSection>

      <EditorSection
        description="This is the main document copy the candidate reads before signing."
        title="Agreement content"
      >
        <div className="space-y-4">
          <TextAreaField
            label="Agreement body"
            minHeightClassName="min-h-56"
            onChange={(value) => onChange("agreementBody", value)}
            value={templateForm.agreementBody}
          />
          <TextAreaField
            label="Terms and conditions"
            minHeightClassName="min-h-40"
            onChange={(value) => onChange("termsAndConditions", value)}
            value={templateForm.termsAndConditions}
          />
          <TextAreaField
            label="Data protection notice"
            minHeightClassName="min-h-36"
            onChange={(value) => onChange("dataProtectionNotice", value)}
            value={templateForm.dataProtectionNotice}
          />
        </div>
      </EditorSection>

      <EditorSection
        description="These defaults are shown in the agreement when a candidate has not entered their own value yet."
        title="Default values"
      >
        <div className="grid gap-4 md:grid-cols-3">
          <TextField
            label="Working hours"
            onChange={(value) => onChange("defaultWorkingHours", value)}
            value={templateForm.defaultWorkingHours}
          />
          <TextField
            label="Confidentiality period"
            onChange={(value) => onChange("defaultConfidentialityPeriod", value)}
            value={templateForm.defaultConfidentialityPeriod}
          />
          <TextField
            label="Jurisdiction city"
            onChange={(value) => onChange("defaultJurisdictionCity", value)}
            value={templateForm.defaultJurisdictionCity}
          />
        </div>
      </EditorSection>
    </div>
  );
}

function EditorSection({
  children,
  description,
  title
}: {
  children: ReactNode;
  description: string;
  title: string;
}) {
  return (
    <section className="border-t border-line/80 pt-8 first:border-t-0 first:pt-0">
      <div className="max-w-3xl">
        <h3 className="text-xl font-semibold tracking-tight text-ink">{title}</h3>
        <p className="mt-2 text-sm leading-7 text-slate">{description}</p>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function DocumentPreviewPanel({
  agreement,
  candidate,
  description,
  templateVersion,
  title
}: {
  agreement: CandidateAgreement;
  candidate: {
    email: string;
    fullName: string;
    phone: string;
  };
  description: string;
  templateVersion: TemplateVersion;
  title: string;
}) {
  return (
    <section>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <p className="eyebrow">Preview</p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink">{title}</h3>
          <p className="mt-3 text-sm leading-7 text-slate">{description}</p>
        </div>

        <AgreementPreviewDownloadButton
          agreement={agreement}
          candidate={candidate}
          templateVersion={templateVersion}
        />
      </div>

      <div className="mt-5">
        <AgreementPreview
          agreement={agreement}
          candidate={candidate}
          templateVersion={templateVersion}
        />
      </div>
    </section>
  );
}

function EditorModeButton({
  active,
  description,
  label,
  onClick
}: {
  active: boolean;
  description: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className={cn(
        "flex min-w-[220px] flex-1 flex-col rounded-[18px] border px-4 py-4 text-left transition",
        active
          ? "border-navy bg-navy text-white shadow-lg shadow-navy/10"
          : "border-line/80 bg-white text-ink hover:border-royal/20 hover:bg-cloud/20"
      )}
      onClick={onClick}
      type="button"
    >
      <span className={cn("text-[11px] font-semibold uppercase tracking-[0.18em]", active ? "text-white/70" : "text-slate/70")}>
        Workspace
      </span>
      <span className="mt-2 text-sm font-semibold">{label}</span>
      <span className={cn("mt-2 text-sm leading-6", active ? "text-white/85" : "text-slate")}>
        {description}
      </span>
    </button>
  );
}

function buildFormPreviewAgreement(
  templateForm: AdminTemplateForm,
  previewValues: ReturnType<typeof buildAgreementFormDefaults>
): CandidateAgreement {
  return {
    ...previewValues,
    agreementDate: "2025-07-05",
    cityState: "Mysore, Karnataka",
    companyAddress: templateForm.companyAddress,
    confidentialityPeriod: templateForm.defaultConfidentialityPeriod,
    course: "Bachelor of Engineering",
    endDate: "2026-01-05",
    internAddress: "Mysore, Karnataka",
    internName: "Ganesh Sabale",
    internshipType: "paid",
    jurisdictionCity: templateForm.defaultJurisdictionCity,
    notes: "",
    signatureLocation: "Mysore",
    signatureName: "Ganesh Sabale",
    signedAt: "2025-07-05",
    startDate: "2025-07-07",
    stipendAmount: "15000",
    workingHours: templateForm.defaultWorkingHours,
    yearOfStudy: "Final year"
  };
}

function buildPreviewTemplateVersion(
  templateForm: AdminTemplateForm,
  sourceVersion: TemplateVersion
): TemplateVersion {
  return {
    ...sourceVersion,
    agreementBody: templateForm.agreementBody,
    companyAddress: templateForm.companyAddress,
    companyName: templateForm.companyName,
    dataProtectionNotice: templateForm.dataProtectionNotice,
    defaults: {
      confidentialityPeriod: templateForm.defaultConfidentialityPeriod,
      jurisdictionCity: templateForm.defaultJurisdictionCity,
      workingHours: templateForm.defaultWorkingHours
    },
    description: templateForm.description,
    formSchema: templateForm.formSchema,
    termsAndConditions: templateForm.termsAndConditions,
    title: templateForm.title
  };
}

function countSchemaFieldBlocks(schema: AdminTemplateForm["formSchema"]) {
  return schema.sections.reduce(
    (count, section) =>
      count +
      section.blocks.filter(
        (block) => "fieldKey" in block || block.type === "signature"
      ).length,
    0
  );
}

function hasDocumentContent(templateForm: AdminTemplateForm) {
  return [
    templateForm.title,
    templateForm.companyName,
    templateForm.companyAddress,
    templateForm.agreementBody,
    templateForm.termsAndConditions,
    templateForm.dataProtectionNotice
  ].some((value) => value.trim().length > 0);
}

function TemplateVersionCard({
  activeVersionId,
  deletingVersionId,
  onDelete,
  version
}: {
  activeVersionId: string;
  deletingVersionId: string | null;
  onDelete: (versionId: string) => Promise<void>;
  version: TemplateVersion;
}) {
  const isActive = version.id === activeVersionId;
  const isDeleting = deletingVersionId === version.id;
  const canDelete = !isActive && !isDeleting;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  function handleDeleteClick() {
    if (!canDelete) {
      return;
    }

    setIsDeleteDialogOpen(true);
  }

  function getDeleteTooltip() {
    if (isActive) return "Set another version as active before deleting this one";
    return "Delete";
  }

  async function handleConfirmDelete() {
    await onDelete(version.id);
    setIsDeleteDialogOpen(false);
  }
  const previewTone = getTemplatePreviewTone(version.status);
  const createdLabel = formatDisplayDate(version.createdAt);
  const publishedLabel = version.publishedAt
    ? formatDisplayDate(version.publishedAt)
    : "Not published";
  const audienceLabel =
    version.assignedCandidateCount > 0
      ? `Assigned to ${formatAssignedCandidateLabel(version.assignedCandidateCount)}.`
      : isActive
        ? "This form is currently in use for all approved users."
        : null;

  return (
    <div className="group overflow-hidden rounded-[24px] border border-line/80 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(17,24,39,0.1)]">
      <div className={`px-4 py-4 ${previewTone.shellClassName}`}>
        <div className="rounded-[20px] border border-white/80 bg-white p-4 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#d89f00]">Gupio</p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate/60">
            Legal agreement
          </p>

          <div className="mt-4 space-y-2.5">
            <div className="h-2.5 w-3/4 rounded-full bg-slate-300/75" />
            <div className="h-2.5 w-full rounded-full bg-slate-200/85" />
            <div className="h-px bg-slate-200/90" />
            <div className="h-2.5 w-5/6 rounded-full bg-slate-200/90" />
            <div className="h-2.5 w-2/3 rounded-full bg-slate-200/75" />
            <div className="h-px bg-slate-200/90" />
            <div className="h-2.5 w-4/5 rounded-full bg-slate-200/75" />
            <div className="h-2.5 w-full rounded-full bg-slate-200/80" />
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-semibold text-ink">{version.title}</p>
            <p className="mt-2 min-h-[3rem] text-sm leading-6 text-slate">
              {version.description || "Agreement form ready for review and version management."}
            </p>
          </div>
          <StatusPill status={version.status} />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-[18px] border border-line/70 bg-cloud/45 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
              Created
            </p>
            <p className="mt-2 text-sm font-semibold leading-6 text-ink">{createdLabel}</p>
          </div>
          <div className="rounded-[18px] border border-line/70 bg-cloud/45 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
              Published
            </p>
            <p className="mt-2 text-sm font-semibold leading-6 text-ink">{publishedLabel}</p>
          </div>
          <div className="rounded-[18px] border border-line/70 bg-cloud/45 px-4 py-3 sm:col-span-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
              Submitted Agreements
            </p>
            <p className="mt-2 text-sm font-semibold leading-6 text-ink">
              {version.submittedCount ?? 0}
            </p>
          </div>
        </div>

        {audienceLabel ? (
          <div
            className={cn(
              "mt-4 rounded-[18px] border px-4 py-3",
              version.assignedCandidateCount > 0
                ? "border-emerald-200 bg-emerald-50/70"
                : "border-line/70 bg-cloud/35"
            )}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
              Audience
            </p>
            <p className="mt-2 text-sm font-semibold text-ink">{audienceLabel}</p>
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-2">
          <div className="flex flex-wrap gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  aria-label={`View ${version.title}`}
                  className={getIconActionButtonClassName()}
                  to={buildTemplateVersionPath(version.id, "view")}
                >
                  <Eye className="h-4 w-4" />
                  <span className="sr-only">View</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent>View</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  aria-label={`Edit ${version.title}`}
                  className={getIconActionButtonClassName()}
                  to={buildTemplateVersionPath(version.id, "edit")}
                >
                  <SquarePen className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={`Delete ${version.title}`}
                  className={getIconActionButtonClassName("danger")}
                  disabled={!canDelete}
                  onClick={handleDeleteClick}
                  type="button"
                >
                  {isDeleting ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  <span className="sr-only">Delete</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>{getDeleteTooltip()}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <DeleteConfirmationDialog
        confirmLabel="Delete form"
        description={`This will permanently delete "${version.title}" along with all its submissions. This action cannot be undone.`}
        isDeleting={isDeleting}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        open={isDeleteDialogOpen}
        testId={`admin-delete-template-version-dialog-${version.id}`}
        title="Delete form?"
      />
    </div>
  );
}

function getTemplatePreviewTone(status: TemplateVersion["status"]) {
  switch (status) {
    case "published":
      return {
        shellClassName: "bg-gradient-to-br from-emerald-50 via-white to-cyan-50"
      };
    case "archived":
      return {
        shellClassName: "bg-gradient-to-br from-slate-100 via-white to-slate-50"
      };
    default:
      return {
        shellClassName: "bg-gradient-to-br from-amber-50 via-white to-orange-50"
      };
  }
}

function getTemplateVersionOptionLabel(version: TemplateVersion) {
  switch (version.status) {
    case "published":
      return version.assignedCandidateCount > 0
        ? `Published for ${formatAssignedCandidateLabel(version.assignedCandidateCount)} - ${formatDisplayDate(version.publishedAt ?? version.createdAt)}`
        : `Published form - ${formatDisplayDate(version.publishedAt ?? version.createdAt)}`;
    case "archived":
      return `Archived copy - ${formatDisplayDate(version.createdAt)}`;
    default:
      return `Draft saved - ${formatDisplayDate(version.createdAt)}`;
  }
}

function formatAssignedCandidateLabel(count: number) {
  return `${count} selected user${count === 1 ? "" : "s"}`;
}

function getUserInitials(name: string) {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) {
    return "NA";
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("");
}

function getIconActionButtonClassName(
  variant: "default" | "danger" | "success" = "default"
) {
  return cn(
    "inline-flex h-10 w-10 items-center justify-center rounded-xl border bg-white shadow-sm transition duration-200",
    "focus:outline-none focus-visible:ring-4 focus-visible:ring-royal/10 disabled:cursor-not-allowed disabled:opacity-50",
    variant === "danger"
      ? "border-rose-200 text-rose-600 hover:-translate-y-0.5 hover:bg-rose-50 hover:text-rose-700"
      : variant === "success"
        ? "border-emerald-200 text-emerald-700 hover:-translate-y-0.5 hover:bg-emerald-50 hover:text-emerald-800"
        : "border-line/80 text-slate hover:-translate-y-0.5 hover:border-royal/20 hover:bg-cloud/60 hover:text-navy"
  );
}

function getCandidateDecisionDialogCopy(
  candidateName: string,
  decision: "approved" | "rejected"
) {
  if (decision === "approved") {
    return {
      confirmLabel: "Approve candidate",
      description: `Are you sure you want to approve ${candidateName}'s account and move the record into the main Users list?`,
      notice:
        "This action updates the account status to approved and removes the candidate from the pending review queue.",
      title: "Approve candidate account?"
    };
  }

  return {
    confirmLabel: "Reject candidate",
    description: `Are you sure you want to reject ${candidateName}'s account and close the current approval request?`,
    notice:
      "This action updates the account status to rejected and removes the candidate from the pending review queue.",
    title: "Reject candidate account?"
  };
}

function buildTemplateVersionPath(versionId: string, mode: "edit" | "view") {
  return `/admin/edit-form?versionId=${encodeURIComponent(versionId)}&mode=${mode}`;
}

export function formatDisplayDate(value?: string) {
  if (!value) {
    return "Not available";
  }

  const timestamp = Date.parse(value);

  if (Number.isNaN(timestamp)) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(timestamp));
}

export function formatPreciseDisplayDate(value?: string) {
  if (!value) {
    return "Not available";
  }

  const timestamp = Date.parse(value);

  if (Number.isNaN(timestamp)) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short"
  }).format(new Date(timestamp));
}


// ─── Candidate Agreements List ────────────────────────────────────────────────

type AgreementTypeFilter = "all" | "internship" | "addendum" | "pending";

interface FlatSubmissionItem {
  candidate: CandidateUser;
  submission: AgreementSubmissionSummary;
  agreementType: "internship" | "addendum";
}

function AgreementTypeBadge({ type }: { type: "internship" | "addendum" }) {
  return type === "internship" ? (
    <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700">
      Internship
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-700">
      Addendum
    </span>
  );
}

function SubmissionStatusBadge({ sub }: { sub: AgreementSubmissionSummary }) {
  if (sub.status === "draft") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-line/60 bg-cloud/70 px-3 py-1 text-xs font-semibold text-slate">
        Draft — not submitted
      </span>
    );
  }
  if (sub.approvalStatus === "approved") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Approved
      </span>
    );
  }
  if (sub.approvalStatus === "rejected") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
        <XCircle className="h-3 w-3" /> Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
      Pending review
    </span>
  );
}

function statusBorderClass(sub: AgreementSubmissionSummary) {
  if (sub.status === "draft") return "border-l-4 border-l-line/60";
  if (sub.approvalStatus === "approved") return "border-l-4 border-l-emerald-400";
  if (sub.approvalStatus === "rejected") return "border-l-4 border-l-rose-400";
  return "border-l-4 border-l-amber-400";
}

// ─── Agreement Detail View ────────────────────────────────────────────────────

interface AgreementDetailViewProps {
  item: FlatSubmissionItem;
  detail: AdminSubmissionDetail | null;
  isLoadingDetail: boolean;
  isReviewing: string | null;
  feedback: string;
  signatoryName: string;
  signatoryDesignation: string;
  decisionError: string | null;
  onBack: () => void;
  onFeedbackChange: (v: string) => void;
  onSignatoryNameChange: (v: string) => void;
  onSignatoryDesignationChange: (v: string) => void;
  onDecision: (decision: "approved" | "rejected") => void;
}

function AgreementDetailView({
  item,
  detail,
  isLoadingDetail,
  isReviewing,
  feedback,
  signatoryName,
  signatoryDesignation,
  decisionError,
  onBack,
  onFeedbackChange,
  onSignatoryNameChange,
  onSignatoryDesignationChange,
  onDecision
}: AgreementDetailViewProps) {
  const { candidate, submission, agreementType } = item;
  const [previewMode, setPreviewMode] = useState<"screen" | "pdf">("screen");
  const [detailPdfUrl, setDetailPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!detail?.pdfBase64) {
      setDetailPdfUrl(null);
      return;
    }

    try {
      const bytes = Uint8Array.from(atob(detail.pdfBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setDetailPdfUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } catch (e) {
      console.error("Failed to generate PDF Blob URL for details", e);
      setDetailPdfUrl(null);
    }
  }, [detail?.pdfBase64]);

  const isReviewable =
    submission.status === "submitted" && submission.approvalStatus === "pending";

  function downloadPdf() {
    if (!detail?.pdfBase64 || !detail.pdfFileName) return;
    const bytes = Uint8Array.from(atob(detail.pdfBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = detail.pdfFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mt-6 space-y-5">
      {/* Back bar */}
      <div className="flex items-center gap-3">
        <button
          className="btn-secondary px-4 py-2.5"
          onClick={onBack}
          type="button"
        >
          ← Back to list
        </button>
        <AgreementTypeBadge type={agreementType} />
      </div>

      {/* Candidate header */}
      <section className="surface px-6 py-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="eyebrow">Agreement Review</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-ink">
              {candidate.fullName}
            </h2>
            <p className="mt-1 text-sm text-slate">{candidate.email}</p>
            {candidate.phone ? (
              <p className="text-sm text-slate">{candidate.phone}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={candidate.status} />
            <SubmissionStatusBadge sub={submission} />
            <span className="text-sm text-slate">v{submission.versionNumber}</span>
          </div>
        </div>

        {submission.submittedAt ? (
          <p className="mt-3 text-sm text-slate">
            Submitted on {formatDisplayDate(submission.submittedAt)}
          </p>
        ) : (
          <p className="mt-3 text-sm text-slate">
            Last updated {formatDisplayDate(submission.updatedAt)}
          </p>
        )}

        {submission.approvalStatus === "approved" && submission.approvedAt ? (
          <p className="mt-1 text-sm font-semibold text-emerald-700">
            Approved on {formatDisplayDate(submission.approvedAt)}
          </p>
        ) : null}

        {submission.approvalStatus === "rejected" && submission.rejectionReason ? (
          <div className="mt-3 rounded-[14px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="font-semibold">Rejection reason:</span> {submission.rejectionReason}
          </div>
        ) : null}
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
        {/* Agreement preview */}
        <section className="surface p-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2">
              <button
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                  previewMode === "screen"
                    ? "border-navy bg-navy text-white shadow-sm"
                    : "border-line bg-cloud/60 text-slate hover:bg-white hover:text-ink"
                }`}
                onClick={() => setPreviewMode("screen")}
                type="button"
              >
                Screen view
              </button>
              {detail?.pdfBase64 ? (
                <button
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                    previewMode === "pdf"
                      ? "border-navy bg-navy text-white shadow-sm"
                      : "border-line bg-cloud/60 text-slate hover:bg-white hover:text-ink"
                  }`}
                  onClick={() => setPreviewMode("pdf")}
                  type="button"
                >
                  PDF view
                </button>
              ) : null}
            </div>
            {detail?.pdfBase64 ? (
              <button
                className="btn-secondary px-4 py-2 text-sm"
                onClick={downloadPdf}
                type="button"
              >
                Download PDF
              </button>
            ) : null}
          </div>

          <div className="mt-4 overflow-hidden rounded-[24px] border border-line/70 bg-white">
            {isLoadingDetail ? (
              <div className="flex items-center justify-center gap-3 py-20 text-slate">
                <LoaderCircle className="h-5 w-5 animate-spin" />
                <span className="text-sm font-medium">Loading agreement…</span>
              </div>
            ) : detail ? (
              previewMode === "screen" ? (
                <div className="max-h-[72vh] overflow-auto p-6">
                  <AgreementPreview
                    agreement={{
                      ...((detail.formData ?? {}) as CandidateAgreement),
                      signatureImageData: (detail as any).signatureImageData
                    }}
                    candidate={{
                      email: (detail as any).candidate?.email ?? candidate.email,
                      fullName: (detail as any).candidate?.fullName ?? candidate.fullName,
                      phone: (detail as any).candidate?.phone ?? candidate.phone ?? "",
                      submittedAt: submission.submittedAt
                    }}
                    showExecutionBlock={true}
                    templateVersion={(detail as any).templateVersion}
                  />
                </div>
              ) : detail.pdfBase64 ? (
                <iframe
                  className="h-[72vh] w-full"
                  src={detailPdfUrl || ""}
                  title="Agreement PDF"
                />
              ) : (
                <div className="px-6 py-10 text-center text-sm text-slate">
                  PDF not available yet.
                </div>
              )
            ) : (
              <div className="px-6 py-10 text-center text-sm text-slate">
                Unable to load agreement content.
              </div>
            )}
          </div>
        </section>

        {/* Review panel */}
        <aside className="space-y-5">
          <div className="surface p-6">
            <p className="eyebrow">Decision</p>
            <h3 className="mt-1 text-xl font-semibold tracking-tight text-ink">
              {isReviewable
                ? "Review this agreement"
                : submission.approvalStatus === "approved"
                  ? "Agreement approved"
                  : submission.approvalStatus === "rejected"
                    ? "Agreement rejected"
                    : "Not submitted yet"}
            </h3>

            {decisionError ? (
              <div className="mt-4 rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                {decisionError}
              </div>
            ) : null}

            {isReviewable ? (
              <div className="mt-5 space-y-4">
                <label className="block space-y-2">
                  <span className="field-label">Feedback for candidate (optional)</span>
                  <textarea
                    className="input-field min-h-28"
                    onChange={(e) => onFeedbackChange(e.target.value)}
                    placeholder="Add notes or required changes visible to the candidate."
                    value={feedback}
                  />
                </label>

                {agreementType === "addendum" ? (
                  <div className="space-y-3 rounded-[18px] border border-line/60 bg-cloud/40 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
                      Authorized Signatory — required to approve
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="block space-y-1.5">
                        <span className="field-label">Signatory name</span>
                        <input
                          className="input-field"
                          onChange={(e) => onSignatoryNameChange(e.target.value)}
                          placeholder="Name"
                          value={signatoryName}
                        />
                      </label>
                      <label className="block space-y-1.5">
                        <span className="field-label">Designation</span>
                        <input
                          className="input-field"
                          onChange={(e) => onSignatoryDesignationChange(e.target.value)}
                          placeholder="Designation"
                          value={signatoryDesignation}
                        />
                      </label>
                    </div>
                  </div>
                ) : null}

                <div className="flex gap-3">
                  <button
                    className="btn-secondary flex-1 py-2.5"
                    disabled={!!isReviewing}
                    onClick={() => onDecision("rejected")}
                    type="button"
                  >
                    {isReviewing === submission.id ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    Reject
                  </button>
                  <button
                    className="btn-primary flex-1 py-2.5"
                    disabled={!!isReviewing}
                    onClick={() => onDecision("approved")}
                    type="button"
                  >
                    {isReviewing === submission.id ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    Approve
                  </button>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate">
                {submission.status === "draft"
                  ? "The candidate has saved a draft but not yet submitted this agreement."
                  : submission.approvalStatus === "approved"
                    ? "This agreement has already been approved. No further action is needed."
                    : "This agreement was rejected. The candidate may request an edit."}
              </p>
            )}
          </div>

          {/* Quick summary */}
          <div className="surface p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
              Agreement Summary
            </p>
            <dl className="mt-4 space-y-2 text-sm">
              {submission.summary.startDate ? (
                <>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate/70">Start date</dt>
                    <dd className="font-medium text-ink">{submission.summary.startDate}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate/70">End date</dt>
                    <dd className="font-medium text-ink">{submission.summary.endDate ?? "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate/70">Type</dt>
                    <dd className="font-medium capitalize text-ink">
                      {submission.summary.internshipType ?? "—"}
                    </dd>
                  </div>
                  {submission.summary.stipendAmount ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-slate/70">Stipend</dt>
                      <dd className="font-medium text-ink">{submission.summary.stipendAmount}</dd>
                    </div>
                  ) : null}
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate/70">Working hours</dt>
                    <dd className="font-medium text-ink">{submission.summary.workingHours ?? "—"}</dd>
                  </div>
                </>
              ) : null}
              {submission.summary.signatureName ? (
                <>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate/70">Signatory</dt>
                    <dd className="font-medium text-ink">{submission.summary.signatureName}</dd>
                  </div>
                  {submission.summary.signatureLocation ? (
                    <div className="flex justify-between gap-2">
                      <dt className="text-slate/70">Location</dt>
                      <dd className="font-medium text-ink">{submission.summary.signatureLocation}</dd>
                    </div>
                  ) : null}
                </>
              ) : null}
              {submission.summary.cityState ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-slate/70">City / State</dt>
                  <dd className="font-medium text-ink">{submission.summary.cityState}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AdminCandidateAgreementsPage() {
  const [overview, setOverview] = useState<CandidateAgreementsRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<AgreementTypeFilter>("all");
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 15;

  // Detail view state
  const [selectedItem, setSelectedItem] = useState<FlatSubmissionItem | null>(null);
  const [detail, setDetail] = useState<AdminSubmissionDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isReviewing, setIsReviewing] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [decisionError, setDecisionError] = useState<string | null>(null);

  async function load() {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await fetchCandidateAgreementsOverview();
      setOverview(response.candidates);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Unable to load agreements.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Build flat list: one entry per filled agreement per candidate
  const flatItems = useMemo<FlatSubmissionItem[]>(() => {
    const items: FlatSubmissionItem[] = [];
    for (const row of overview) {
      if (row.internshipSubmission) {
        items.push({
          candidate: row.candidate,
          submission: row.internshipSubmission,
          agreementType: "internship"
        });
      }
      if (row.addendumSubmission) {
        items.push({
          candidate: row.candidate,
          submission: row.addendumSubmission,
          agreementType: "addendum"
        });
      }
    }
    // Sort: pending first, then by submittedAt desc
    return items.sort((a, b) => {
      const aPending = a.submission.approvalStatus === "pending" ? 0 : 1;
      const bPending = b.submission.approvalStatus === "pending" ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      const aDate = a.submission.submittedAt ?? a.submission.updatedAt;
      const bDate = b.submission.submittedAt ?? b.submission.updatedAt;
      return bDate.localeCompare(aDate);
    });
  }, [overview]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filtered = useMemo(() => {
    let rows = flatItems;

    if (normalizedQuery) {
      rows = rows.filter((item) =>
        [item.candidate.fullName, item.candidate.email, item.candidate.phone ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery)
      );
    }

    if (typeFilter === "internship") {
      rows = rows.filter((item) => item.agreementType === "internship");
    } else if (typeFilter === "addendum") {
      rows = rows.filter((item) => item.agreementType === "addendum");
    } else if (typeFilter === "pending") {
      rows = rows.filter((item) => item.submission.approvalStatus === "pending");
    }

    return rows;
  }, [flatItems, normalizedQuery, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPageIndex = Math.min(pageIndex, totalPages - 1);
  const pageStart = currentPageIndex * pageSize;
  const visibleItems = filtered.slice(pageStart, pageStart + pageSize);
  const resultStart = filtered.length === 0 ? 0 : pageStart + 1;
  const resultEnd = Math.min(pageStart + visibleItems.length, filtered.length);

  const pendingCount = flatItems.filter((i) => i.submission.approvalStatus === "pending").length;
  const internshipCount = flatItems.filter((i) => i.agreementType === "internship").length;
  const addendumCount = flatItems.filter((i) => i.agreementType === "addendum").length;
  const approvedCount = flatItems.filter((i) => i.submission.approvalStatus === "approved").length;

  async function openDetail(item: FlatSubmissionItem) {
    setSelectedItem(item);
    setDetail(null);
    setDecisionError(null);
    setFeedback(item.submission.reviewComment ?? "");
    setSignatoryName("");
    setSignatoryDesignation("");
    setIsLoadingDetail(true);
    try {
      const data = await fetchAdminSubmissionDetail(item.submission.id);
      setDetail(data);
      if (item.agreementType === "addendum") {
        const fd = (data.formData ?? {}) as Record<string, unknown>;
        if (typeof (fd as any).authorizedSignatoryName === "string")
          setSignatoryName((fd as any).authorizedSignatoryName);
        if (typeof (fd as any).authorizedSignatoryDesignation === "string")
          setSignatoryDesignation((fd as any).authorizedSignatoryDesignation);
      }
    } catch (err) {
      setDecisionError(err instanceof Error ? err.message : "Failed to load agreement.");
    } finally {
      setIsLoadingDetail(false);
    }
  }

  async function handleDecision(decision: "approved" | "rejected") {
    if (!selectedItem) return;
    setIsReviewing(selectedItem.submission.id);
    setDecisionError(null);
    try {
      await reviewAgreementSubmission(selectedItem.submission.id, {
        decision,
        rejectionReason:
          decision === "rejected" ? feedback || "Rejected by administrator" : undefined,
        comment: feedback || undefined,
        authorizedSignatoryName:
          decision === "approved" ? signatoryName || undefined : undefined,
        authorizedSignatoryDesignation:
          decision === "approved" ? signatoryDesignation || undefined : undefined
      });
      // Refresh data and update selected item
      const response = await fetchCandidateAgreementsOverview();
      setOverview(response.candidates);
      // Find the updated version of the selected item
      const updatedRow = response.candidates.find(
        (r) => r.candidate.id === selectedItem.candidate.id
      );
      if (updatedRow) {
        const updatedSub =
          selectedItem.agreementType === "internship"
            ? updatedRow.internshipSubmission
            : updatedRow.addendumSubmission;
        if (updatedSub) {
          setSelectedItem({
            ...selectedItem,
            submission: updatedSub
          });
        }
      }
      // Re-fetch detail to get updated approval fields
      const updatedDetail = await fetchAdminSubmissionDetail(selectedItem.submission.id);
      setDetail(updatedDetail);
    } catch (err) {
      setDecisionError(err instanceof Error ? err.message : "Action failed. Please try again.");
    } finally {
      setIsReviewing(null);
    }
  }

  // ── Detail view ──
  if (selectedItem) {
    return (
      <AgreementDetailView
        decisionError={decisionError}
        detail={detail}
        feedback={feedback}
        isLoadingDetail={isLoadingDetail}
        isReviewing={isReviewing}
        item={selectedItem}
        onBack={() => setSelectedItem(null)}
        onDecision={handleDecision}
        onFeedbackChange={setFeedback}
        onSignatoryDesignationChange={setSignatoryDesignation}
        onSignatoryNameChange={setSignatoryName}
        signatoryDesignation={signatoryDesignation}
        signatoryName={signatoryName}
      />
    );
  }

  // ── List view ──
  return (
    <div className="mt-6 space-y-6" data-testid="admin-candidate-agreements-page">
      {/* Header */}
      <section className="surface px-6 py-6 md:px-7">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="eyebrow">Agreements</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
              Candidate agreements
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate">
              Every agreement submitted by candidates — internship and addendum. Click on any
              agreement to read it in full and approve or reject.
            </p>
          </div>
          <button
            className="btn-secondary px-4 py-2.5"
            onClick={() => void load()}
            type="button"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </section>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Total Filled", value: flatItems.length },
          { label: "Internship", value: internshipCount },
          { label: "Addendum", value: addendumCount },
          { label: "Pending Review", value: pendingCount }
        ].map((s) => (
          <div className="surface px-5 py-5" key={s.label}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">
              {s.label}
            </p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-ink">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter + Search */}
      <section className="surface p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Filter tabs */}
          <div
            aria-label="Filter"
            className="inline-flex flex-wrap gap-1.5 rounded-[18px] border border-line/70 bg-cloud/45 p-1"
            role="tablist"
          >
            {(
              [
                { key: "all", label: "All agreements", count: flatItems.length },
                { key: "internship", label: "Internship", count: internshipCount },
                { key: "addendum", label: "Addendum", count: addendumCount },
                { key: "pending", label: "Pending review", count: pendingCount }
              ] as { key: AgreementTypeFilter; label: string; count: number }[]
            ).map((tab) => (
              <button
                aria-selected={typeFilter === tab.key}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  typeFilter === tab.key
                    ? "bg-navy text-white shadow-sm"
                    : "text-slate hover:bg-white/80 hover:text-ink"
                }`}
                key={tab.key}
                onClick={() => {
                  setTypeFilter(tab.key);
                  setPageIndex(0);
                }}
                role="tab"
                type="button"
              >
                {tab.label}
                <span className="ml-1.5 text-xs opacity-70">{tab.count}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <label className="block w-full max-w-sm">
            <span className="sr-only">Search candidates</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/70" />
              <input
                className="input-field pl-11"
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPageIndex(0);
                }}
                placeholder="Search by name, email, or phone"
                type="search"
                value={searchQuery}
              />
            </div>
          </label>
        </div>

        {loadError ? (
          <div className="mt-5 rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700">
            {loadError}
          </div>
        ) : null}

        {/* List */}
        <div className="mt-5 space-y-0 overflow-hidden rounded-[24px] border border-line/80 bg-white shadow-sm">
          {isLoading ? (
            <div className="flex items-center justify-center gap-3 py-16 text-slate">
              <LoaderCircle className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">Loading agreements…</span>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              hint={
                flatItems.length === 0
                  ? "Agreements will appear here once candidates submit them."
                  : undefined
              }
              text={
                flatItems.length === 0
                  ? "No agreements submitted yet."
                  : "No agreements match the current filter."
              }
            />
          ) : (
            <>
              <div className="divide-y divide-line/70">
                {visibleItems.map((item, index) => (
                  <button
                    className={cn(
                      "group w-full px-5 py-4 text-left transition hover:bg-cloud/40",
                      statusBorderClass(item.submission)
                    )}
                    key={`${item.candidate.id}-${item.agreementType}`}
                    onClick={() => void openDetail(item)}
                    type="button"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate/60">
                            #{pageStart + index + 1}
                          </span>
                          <AgreementTypeBadge type={item.agreementType} />
                          <SubmissionStatusBadge sub={item.submission} />
                        </div>
                        <p className="text-base font-semibold text-ink group-hover:text-royal transition-colors">
                          {item.candidate.fullName}
                        </p>
                        <p className="text-sm text-slate">
                          {item.candidate.email}
                          {item.candidate.phone ? ` · ${item.candidate.phone}` : ""}
                        </p>
                      </div>

                      <div className="flex flex-col items-start gap-2 sm:items-end">
                        <p className="text-sm text-slate">
                          {item.submission.submittedAt
                            ? `Submitted ${formatDisplayDate(item.submission.submittedAt)}`
                            : `Draft — ${formatDisplayDate(item.submission.updatedAt)}`}
                        </p>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-royal/20 bg-royal/5 px-3 py-1 text-xs font-semibold text-royal transition group-hover:bg-royal group-hover:text-white">
                          View agreement →
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex flex-col gap-3 border-t border-line/80 bg-cloud/35 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate">
                  Showing {resultStart}–{resultEnd} of {filtered.length}
                  {filtered.length !== flatItems.length
                    ? ` (filtered from ${flatItems.length})`
                    : ""}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="btn-secondary px-3 py-2 text-xs"
                    disabled={currentPageIndex === 0}
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    type="button"
                  >
                    Previous
                  </button>
                  <span className="rounded-xl border border-line/70 bg-white px-3 py-2 text-xs font-semibold text-ink">
                    Page {currentPageIndex + 1} of {totalPages}
                  </span>
                  <button
                    className="btn-secondary px-3 py-2 text-xs"
                    disabled={currentPageIndex >= totalPages - 1}
                    onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Approved count note */}
        {approvedCount > 0 ? (
          <p className="mt-3 text-xs text-slate/70">
            {approvedCount} agreement{approvedCount === 1 ? "" : "s"} already approved.
          </p>
        ) : null}
      </section>
    </div>
  );
}
